/**
 * Elite Batch 13 Part 4 — Workflow Templates 1208–1227
 * Store-specific winback, subscriber sunset, subscription-canceled winback,
 * suppression list management, TOF/MOF/BOF creative matrix, testimonial
 * collection and compilation, tier-up congratulations, TikTok publishing
 * pipelines, trial signup onboarding and retargeting, UGC creator recruitment
 * and rights management, UTM link building, unboxing video, underperformer
 * pause queue, and unengaged email sunset.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 13 PART 4 — TEMPLATES 1208–1227
// =============================================================================

export const ELITE_BATCH13_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1208. STORE-SPECIFIC WINBACK (8 nodes)
  // ===================================================================

  "Store-Specific Winback": {
    summary:
      "Identifies lapsed store customers from HubSpot, enriches them with store-specific data, uses AI to generate personalized store offers and messages, sends a winback email, follows up with an SMS via Twilio, and notifies the store manager on Slack.",
    nodes: [
      trigger("Schedule: Identify lapsed store customers", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Fires every Monday at 8 AM to scan for customers who have not purchased from a specific store in the last 90 days and initiates a personalized winback campaign",
        setup:
          "Configure HubSpot API token with contact and deal read access. Set up store location data source. Provide Twilio credentials for SMS. Set Slack webhook for store manager notifications.",
        inputFields: [],
      }),

      // 2. HubSpot lapsed store customer identifier
      hubspotGetNode(
        "2",
        "Identify lapsed store customers (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search?hapikey={hubspot_token}&limit=50",
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "customer_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "customer_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "customer_phone",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_last_visit_timestamp",
            customName: "last_visit_date",
            type: "string",
          },
          {
            name: "results[0].properties.store_location",
            customName: "preferred_store",
            type: "string",
          },
          { name: "total", customName: "lapsed_count", type: "number" },
        ],
      ),

      // 3. Store data enricher API
      n("3", "hitApi", "Fetch store details and offers", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourstoreplatform.com/v1/stores/{preferred_store}?include=current_promotions,hours,manager",
          headers: [
            { key: "Authorization", value: "Bearer {store_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "store.name", customName: "store_name", type: "string" },
            {
              name: "store.address",
              customName: "store_address",
              type: "string",
            },
            {
              name: "store.manager_name",
              customName: "store_manager",
              type: "string",
            },
            {
              name: "store.current_promotions[0].title",
              customName: "current_promo",
              type: "string",
            },
            {
              name: "store.current_promotions[0].discount_percent",
              customName: "promo_discount",
              type: "number",
            },
            {
              name: "store.hours.today",
              customName: "store_hours_today",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI store-specific offer generator
      aiNode(
        "4",
        "AI: Generate store-specific winback offer",
        760,
        "You are a retail winback specialist. Given a lapsed customer and their preferred store details including current promotions, craft a compelling store-specific offer that gives them a reason to return. Consider their last visit date to gauge lapse severity. Respond JSON: {offer_headline: string, offer_details: string, offer_code: string, urgency_message: string, personalization_note: string}",
        "Customer: {customer_first_name} {customer_last_name}. Last visit: {last_visit_date}. Store: {store_name} at {store_address}. Current promo: {current_promo} ({promo_discount}% off). Manager: {store_manager}. Hours: {store_hours_today}.",
        [
          { field: "offer_headline", as: "offer_headline", type: "string" },
          { field: "offer_details", as: "offer_details", type: "string" },
          { field: "offer_code", as: "offer_code", type: "string" },
          { field: "urgency_message", as: "urgency_message", type: "string" },
          {
            field: "personalization_note",
            as: "personalization_note",
            type: "string",
          },
        ],
      ),

      // 5. AI personalized email message
      aiNode(
        "5",
        "AI: Compose personalized winback email",
        1000,
        "You are an email copywriter specializing in retail winback. Write a warm, personal email from the store manager to a lapsed customer. Include the store-specific offer, create urgency, and make it feel like a personal invitation rather than a marketing email. Respond JSON: {email_subject: string, email_body_html: string, preview_text: string}",
        "Customer name: {customer_first_name}. Store: {store_name}. Manager: {store_manager}. Offer: {offer_headline} - {offer_details}. Code: {offer_code}. Urgency: {urgency_message}. Personal note: {personalization_note}.",
        [
          { field: "email_subject", as: "winback_subject", type: "string" },
          { field: "email_body_html", as: "winback_body", type: "string" },
          { field: "preview_text", as: "winback_preview", type: "string" },
        ],
      ),

      // 6. Send winback email
      emailNode(
        "6",
        "Send store winback email",
        1240,
        "{customer_email}",
        "{winback_subject}",
        "{winback_body}",
      ),

      // 7. SMS follow-up (Twilio)
      n("7", "hitApi", "Send SMS follow-up (Twilio)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{customer_phone}" },
            { key: "From", value: "{twilio_phone_number}" },
            {
              key: "Body",
              value:
                "Hi {customer_first_name}! {store_manager} at {store_name} misses you. {offer_headline} - use code {offer_code}. {urgency_message}",
            },
          ],
          variables: [
            { name: "sid", customName: "sms_sid", type: "string" },
            { name: "status", customName: "sms_status", type: "string" },
          ],
        },
      }),

      // 8. Store manager notifier Slack
      slackNode(
        "8",
        "Notify store manager (Slack)",
        1720,
        ":department_store: *Store Winback Sent*\nStore: {store_name}\nCustomer: {customer_first_name} {customer_last_name} ({customer_email})\nLast visit: {last_visit_date}\nOffer: {offer_headline} ({offer_code})\nEmail: Sent | SMS: {sms_status}\nTotal lapsed targeted: {lapsed_count}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1209. SUBSCRIBER INACTIVE NO OPENS (7 nodes)
  // ===================================================================

  "Subscriber Inactive No Opens": {
    summary:
      "Scans HubSpot for subscribers with zero email opens in the past 60 days, checks deliverability status, uses AI to generate attention-grabbing subject lines, sends a re-engagement email, waits 5 days, and routes the outcome to either restore the subscriber or suppress them based on engagement.",
    nodes: [
      trigger("Schedule: Scan for inactive subscribers", "schedule", {
        schedule: "0 7 * * 3",
        howItStarts:
          "Fires every Wednesday at 7 AM to identify email subscribers who have not opened any emails in the past 60 days and attempts a re-engagement sequence before suppression",
        setup:
          "Configure HubSpot API token with contact and email analytics read access. Set email sending domain. Define the open-rate threshold and suppression criteria.",
        inputFields: [],
      }),

      // 2. HubSpot inactivity scanner
      hubspotPostNode(
        "2",
        "Search inactive subscribers (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_email_last_open_date","operator":"LT","value":"{sixty_days_ago_epoch}"},{"propertyName":"hs_email_optout","operator":"EQ","value":"false"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","hs_email_last_open_date","hs_email_sends_since_last_engagement","hs_email_bounce","lifecyclestage"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "subscriber_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "subscriber_name",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "last_open_date",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_sends_since_last_engagement",
            customName: "sends_since_engagement",
            type: "number",
          },
          {
            name: "results[0].properties.hs_email_bounce",
            customName: "bounce_status",
            type: "string",
          },
          { name: "total", customName: "inactive_total", type: "number" },
        ],
      ),

      // 3. Deliverability check condition
      n("3", "condition", "Check deliverability status", 520, {
        conditionData: [
          { variable: "bounce_status", operator: "not_equals", value: "true" },
        ],
      }),

      // 4. AI attention-grabbing subject line
      aiNode(
        "4",
        "AI: Generate attention-grabbing subject line",
        760,
        "You are an email deliverability and re-engagement expert. This subscriber has not opened emails in 60+ days. Generate an irresistible subject line designed to break through inbox blindness. Use curiosity gaps, personalization, or urgency. Also generate a preview text and a short compelling email body that acknowledges the silence and offers value. Respond JSON: {subject_line: string, preview_text: string, email_body_html: string, reengagement_strategy: string}",
        "Subscriber: {subscriber_name} ({subscriber_email}). Last opened: {last_open_date}. Emails sent since last engagement: {sends_since_engagement}.",
        [
          { field: "subject_line", as: "reengagement_subject", type: "string" },
          { field: "preview_text", as: "reengagement_preview", type: "string" },
          { field: "email_body_html", as: "reengagement_body", type: "string" },
          {
            field: "reengagement_strategy",
            as: "reengagement_strategy",
            type: "string",
          },
        ],
      ),

      // 5. Re-engagement email sender
      emailNode(
        "5",
        "Send re-engagement email",
        1000,
        "{subscriber_email}",
        "{reengagement_subject}",
        "{reengagement_body}",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for response", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Outcome router condition (restore or suppress)
      n("7", "condition", "Route outcome: restore or suppress", 1480, {
        conditionData: [
          {
            variable: "hs_email_last_open_date",
            operator: "greater_than",
            value: "{five_days_ago_epoch}",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1210. SUBSCRIPTION-CANCELED WINBACK (9 nodes)
  // ===================================================================

  "Subscription-Canceled Winback": {
    summary:
      "Captures subscription cancellation events via webhook, extracts cancellation data with AI, analyzes the cancellation reason in code, routes by reason type, generates a reason-specific winback email with AI, sends an acknowledgment email, waits 7 days, sends a product updates email, and checks for re-subscription.",
    nodes: [
      trigger("Webhook: Subscription cancellation event", "webhook_app", {
        source: "stripe",
        event: "customer.subscription.deleted",
        howItStarts:
          "Fires when a subscription cancellation event is received from Stripe webhook, initiating a multi-touch winback sequence tailored to the cancellation reason",
        setup:
          "Configure Stripe webhook to send customer.subscription.deleted events. Set up OpenAI API key for content generation. Configure email sending domain.",
        inputFields: [],
      }),

      // 2. AI extract cancellation data
      aiNode(
        "2",
        "AI: Extract cancellation details",
        280,
        "You are a data extraction specialist. Extract subscription cancellation details from this webhook payload regardless of field format. Find the customer email, customer name, subscription plan name, cancellation reason or feedback, subscription start date, monthly amount, and any survey responses. Respond JSON: {customer_email: string, customer_name: string, plan_name: string, cancel_reason: string, subscription_start: string, monthly_amount: string, feedback_text: string, customer_id: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "cancel_reason", as: "cancel_reason", type: "string" },
          {
            field: "subscription_start",
            as: "subscription_start",
            type: "string",
          },
          { field: "monthly_amount", as: "monthly_amount", type: "string" },
          { field: "feedback_text", as: "feedback_text", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
        ],
      ),

      // 3. Cancellation reason analyzer runCode
      n("3", "runCode", "Analyze cancellation reason category", 520, {
        runCodeDetails: {
          code: `// Categorize cancellation reason into actionable buckets
const reason = (variables.cancel_reason || '').toLowerCase();
const feedback = (variables.feedback_text || '').toLowerCase();
const combined = reason + ' ' + feedback;
let category = 'unknown';
let winback_approach = 'general';
if (combined.includes('price') || combined.includes('expensive') || combined.includes('cost') || combined.includes('budget')) {
  category = 'price_sensitive';
  winback_approach = 'discount_offer';
} else if (combined.includes('feature') || combined.includes('missing') || combined.includes('need') || combined.includes('functionality')) {
  category = 'feature_gap';
  winback_approach = 'product_update';
} else if (combined.includes('competitor') || combined.includes('alternative') || combined.includes('switch')) {
  category = 'competitive_loss';
  winback_approach = 'differentiation';
} else if (combined.includes('support') || combined.includes('service') || combined.includes('help') || combined.includes('bug')) {
  category = 'support_issue';
  winback_approach = 'service_recovery';
} else if (combined.includes('not using') || combined.includes('dont use') || combined.includes('unused')) {
  category = 'low_usage';
  winback_approach = 'value_demonstration';
}
const tenure_months = Math.round((Date.now() - new Date(variables.subscription_start).getTime()) / (30 * 24 * 60 * 60 * 1000));
const is_long_tenure = tenure_months > 6;
return { reason_category: category, winback_approach: winback_approach, tenure_months: tenure_months, is_long_tenure: is_long_tenure };`,
          outputs: [
            { name: "reason_category", type: "string" },
            { name: "winback_approach", type: "string" },
            { name: "tenure_months", type: "number" },
            { name: "is_long_tenure", type: "boolean" },
          ],
        },
      }),

      // 4. Reason-specific path router condition
      n("4", "condition", "Route by cancellation reason category", 760, {
        conditionData: [
          {
            variable: "reason_category",
            operator: "not_equals",
            value: "unknown",
          },
        ],
      }),

      // 5. AI winback email generator
      aiNode(
        "5",
        "AI: Generate reason-specific winback email",
        1000,
        "You are a subscription retention specialist. Based on the cancellation reason category and winback approach, write a highly targeted winback email. For price_sensitive: offer a discount or downgrade option. For feature_gap: highlight upcoming features. For competitive_loss: emphasize unique differentiators. For support_issue: apologize and offer dedicated support. For low_usage: share quick-start tips and success stories. Respond JSON: {subject: string, body_html: string, cta_text: string, offer_type: string, offer_value: string}",
        "Customer: {customer_name}. Plan: {plan_name} at ${monthly_amount}/mo. Tenure: {tenure_months} months. Reason: {cancel_reason}. Category: {reason_category}. Approach: {winback_approach}. Feedback: {feedback_text}. Long tenure: {is_long_tenure}.",
        [
          { field: "subject", as: "winback_subject", type: "string" },
          { field: "body_html", as: "winback_body", type: "string" },
          { field: "cta_text", as: "winback_cta", type: "string" },
          { field: "offer_type", as: "winback_offer_type", type: "string" },
          { field: "offer_value", as: "winback_offer_value", type: "string" },
        ],
      ),

      // 6. Email 1: acknowledgment
      emailNode(
        "6",
        "Send cancellation acknowledgment with offer",
        1240,
        "{customer_email}",
        "{winback_subject}",
        "{winback_body}",
      ),

      // 7. Delay 7 days
      n("7", "delayAction", "Wait 7 days before follow-up", 1480, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 8. Email 2: product updates
      emailNode(
        "8",
        "Send product updates follow-up",
        1720,
        "{customer_email}",
        "What you have been missing on {plan_name}, {customer_name}",
        "<h2>We have been busy building</h2><p>Hi {customer_name},</p><p>Since you left, we have shipped several improvements to {plan_name}. Here is what is new:</p><ul><li>Performance improvements across the board</li><li>New features based on customer feedback like yours</li><li>Enhanced support with faster response times</li></ul><p>Your {winback_offer_type} offer ({winback_offer_value}) is still available. We would love to have you back.</p><p><a href='{resubscribe_url}?customer={customer_id}&offer={winback_offer_type}'>{winback_cta}</a></p>",
      ),

      // 9. Re-subscription listener condition
      n("9", "condition", "Check for re-subscription activity", 1960, {
        conditionData: [
          {
            variable: "resubscription_status",
            operator: "equals",
            value: "active",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1211. SUPPRESSION LIST MANAGER (8 nodes)
  // ===================================================================

  "Suppression List Manager": {
    summary:
      "Receives suppression events via webhook, extracts event data with AI, normalizes event types in code, updates the master suppression list in HubSpot, syncs to email platform and Meta Ads for cross-platform compliance, resolves duplicate conflicts via condition, and logs the full audit trail to Google Sheets.",
    nodes: [
      trigger("Webhook: Suppression event received", "webhook_app", {
        source: "email_platform",
        event: "suppression.created",
        howItStarts:
          "Fires when a suppression event (bounce, complaint, unsubscribe) is received from any email platform, ensuring cross-platform compliance and audit logging",
        setup:
          "Configure email platform webhook for bounce, complaint, and unsubscribe events. Set up HubSpot API for master list updates. Provide Meta Ads API token for audience exclusion sync. Set up Google Sheets for audit trail.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI: Extract suppression event data",
        280,
        "You are a data extraction specialist for email compliance. Extract suppression event details from this webhook regardless of format. Find the email address, suppression type (bounce/complaint/unsubscribe), event timestamp, reason or description, originating platform name, and any bounce codes or complaint categories. Respond JSON: {email_address: string, suppression_type: string, event_timestamp: string, reason: string, originating_platform: string, bounce_code: string, complaint_category: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "email_address", as: "suppressed_email", type: "string" },
          { field: "suppression_type", as: "suppression_type", type: "string" },
          { field: "event_timestamp", as: "event_timestamp", type: "string" },
          { field: "reason", as: "suppression_reason", type: "string" },
          {
            field: "originating_platform",
            as: "origin_platform",
            type: "string",
          },
          { field: "bounce_code", as: "bounce_code", type: "string" },
          {
            field: "complaint_category",
            as: "complaint_category",
            type: "string",
          },
        ],
      ),

      // 3. Event normalizer runCode
      n("3", "runCode", "Normalize suppression event data", 520, {
        runCodeDetails: {
          code: `// Normalize suppression types and severity levels
const type = (variables.suppression_type || '').toLowerCase();
const bounceCode = variables.bounce_code || '';
let normalized_type = 'unknown';
let severity = 'low';
let permanent = false;
if (type.includes('hard_bounce') || type.includes('hard bounce') || bounceCode.startsWith('5')) {
  normalized_type = 'hard_bounce';
  severity = 'critical';
  permanent = true;
} else if (type.includes('soft_bounce') || type.includes('soft bounce') || bounceCode.startsWith('4')) {
  normalized_type = 'soft_bounce';
  severity = 'medium';
  permanent = false;
} else if (type.includes('complaint') || type.includes('spam')) {
  normalized_type = 'spam_complaint';
  severity = 'critical';
  permanent = true;
} else if (type.includes('unsubscribe') || type.includes('optout') || type.includes('opt-out')) {
  normalized_type = 'unsubscribe';
  severity = 'high';
  permanent = true;
} else if (type.includes('invalid') || type.includes('not_exist')) {
  normalized_type = 'invalid_address';
  severity = 'critical';
  permanent = true;
}
const suppression_id = 'SUP-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6);
return { normalized_type: normalized_type, severity: severity, is_permanent: permanent, suppression_id: suppression_id };`,
          outputs: [
            { name: "normalized_type", type: "string" },
            { name: "severity", type: "string" },
            { name: "is_permanent", type: "boolean" },
            { name: "suppression_id", type: "string" },
          ],
        },
      }),

      // 4. Master suppression list updater HubSpot
      hubspotPostNode(
        "4",
        "Update master suppression list (HubSpot)",
        760,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{suppressed_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","suppression_status","suppression_type","suppression_date"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.suppression_status",
            customName: "existing_suppression",
            type: "string",
          },
          { name: "total", customName: "contact_match_count", type: "number" },
        ],
      ),

      // 5. Cross-platform sync email API
      n("5", "hitApi", "Sync to email platform suppression list", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.postmarkapp.com/server/suppressions",
          headers: [
            {
              key: "X-Postmark-Server-Token",
              value: "{postmark_server_token}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "Suppressions",
              value:
                '[{"EmailAddress":"{suppressed_email}","SuppressionReason":"{normalized_type}"}]',
            },
          ],
          variables: [
            {
              name: "Suppressions[0].Status",
              customName: "email_sync_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Cross-platform sync ads Meta API
      n("6", "hitApi", "Sync exclusion to Meta Ads audience", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_exclusion_audience_id}/users",
          headers: [
            { key: "Authorization", value: "Bearer {meta_ads_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "payload",
              value:
                '{"schema":"EMAIL_SHA256","data":["{suppressed_email_sha256}"]}',
            },
          ],
          variables: [
            {
              name: "audience_id",
              customName: "meta_audience_id",
              type: "string",
            },
            {
              name: "num_received",
              customName: "meta_records_synced",
              type: "number",
            },
          ],
        },
      }),

      // 7. Duplicate conflict resolver condition
      n("7", "condition", "Check for duplicate suppression conflict", 1480, {
        conditionData: [
          {
            variable: "contact_match_count",
            operator: "greater_than",
            value: "0",
          },
        ],
      }),

      // 8. Audit trail logger Sheets
      sheetsNode(
        "8",
        "Log suppression audit trail (Sheets)",
        1720,
        "Suppression_Audit",
        '[["{suppression_id}","{suppressed_email}","{normalized_type}","{severity}","{is_permanent}","{origin_platform}","{suppression_reason}","{bounce_code}","{email_sync_status}","{meta_records_synced}","{existing_suppression}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1212. TOF/MOF/BOF CREATIVE MATRIX (8 nodes)
  // ===================================================================

  "TOF/MOF/BOF Creative Matrix": {
    summary:
      "Takes a manual campaign brief, loads audience personas via code, uses AI to generate a full-funnel strategy, then generates separate TOF, MOF, and BOF creatives with AI, compiles the matrix into Google Sheets, and distributes review assignments via email.",
    nodes: [
      trigger("Manual: Submit campaign creative brief", "manual", {
        howItStarts:
          "Manually triggered when a marketing team submits a new campaign brief requiring full-funnel creative assets across TOF (awareness), MOF (consideration), and BOF (conversion) stages",
        setup:
          "Prepare campaign brief with product details, target audience, and goals. Configure OpenAI API key. Set up Google Sheets for creative matrix. Configure email for review distribution.",
        inputFields: [
          { name: "campaign_name", type: "string", example: "Q2 SaaS Launch" },
          {
            name: "product_description",
            type: "string",
            example: "AI-powered project management tool for remote teams",
          },
          {
            name: "target_audience",
            type: "string",
            example: "Tech startup founders and project managers, 28-45",
          },
          {
            name: "campaign_goal",
            type: "string",
            example: "Drive 500 trial signups in 30 days",
          },
          {
            name: "brand_tone",
            type: "string",
            example: "Professional yet approachable, data-driven",
          },
          { name: "budget_tier", type: "string", example: "mid" },
        ],
      }),

      // 2. Audience persona loader runCode
      n("2", "runCode", "Load and structure audience personas", 280, {
        runCodeDetails: {
          code: `// Build structured persona profiles from campaign brief
const audience = variables.target_audience || '';
const product = variables.product_description || '';
const goal = variables.campaign_goal || '';
const personas = [];
const segments = audience.split(',').map(s => s.trim());
segments.forEach((seg, i) => {
  personas.push({
    id: 'persona_' + (i + 1),
    description: seg,
    pain_points: 'Needs efficient ' + product.split(' ').slice(0, 3).join(' '),
    buying_stage: i === 0 ? 'awareness' : i === 1 ? 'consideration' : 'decision',
    preferred_channels: i === 0 ? 'social,display' : i === 1 ? 'search,email' : 'retargeting,email',
  });
});
const persona_summary = personas.map(p => p.description + ' (' + p.buying_stage + ')').join('; ');
return { persona_profiles: JSON.stringify(personas), persona_count: personas.length, persona_summary: persona_summary };`,
          outputs: [
            { name: "persona_profiles", type: "string" },
            { name: "persona_count", type: "number" },
            { name: "persona_summary", type: "string" },
          ],
        },
      }),

      // 3. AI funnel strategy generator
      aiNode(
        "3",
        "AI: Generate full-funnel creative strategy",
        520,
        "You are a performance marketing strategist. Given the campaign brief and audience personas, create a full-funnel creative strategy covering TOF (top of funnel - awareness), MOF (middle of funnel - consideration), and BOF (bottom of funnel - conversion). Define the messaging angle, creative format, KPIs, and budget allocation for each stage. Respond JSON: {tof_strategy: string, mof_strategy: string, bof_strategy: string, overall_theme: string, budget_split_tof: number, budget_split_mof: number, budget_split_bof: number, total_creatives_needed: number}",
        "Campaign: {campaign_name}. Product: {product_description}. Audience: {persona_summary}. Goal: {campaign_goal}. Tone: {brand_tone}. Budget tier: {budget_tier}. Personas: {persona_profiles}.",
        [
          { field: "tof_strategy", as: "tof_strategy", type: "string" },
          { field: "mof_strategy", as: "mof_strategy", type: "string" },
          { field: "bof_strategy", as: "bof_strategy", type: "string" },
          { field: "overall_theme", as: "overall_theme", type: "string" },
          { field: "budget_split_tof", as: "budget_tof_pct", type: "number" },
          { field: "budget_split_mof", as: "budget_mof_pct", type: "number" },
          { field: "budget_split_bof", as: "budget_bof_pct", type: "number" },
          {
            field: "total_creatives_needed",
            as: "total_creatives",
            type: "number",
          },
        ],
      ),

      // 4. TOF creative generator AI
      aiNode(
        "4",
        "AI: Generate TOF awareness creatives",
        760,
        "You are a creative director specializing in top-of-funnel awareness ads. Generate 3 TOF creative concepts including: headline, body copy, visual description, CTA, ad format (carousel/video/static), and target platform (Meta/TikTok/Display). Focus on attention-grabbing, problem-awareness messaging. Respond JSON: {tof_creatives: string, tof_headline_1: string, tof_headline_2: string, tof_headline_3: string, tof_formats: string}",
        "Strategy: {tof_strategy}. Product: {product_description}. Audience: {persona_summary}. Theme: {overall_theme}. Tone: {brand_tone}.",
        [
          { field: "tof_creatives", as: "tof_creatives", type: "string" },
          { field: "tof_headline_1", as: "tof_headline_1", type: "string" },
          { field: "tof_headline_2", as: "tof_headline_2", type: "string" },
          { field: "tof_headline_3", as: "tof_headline_3", type: "string" },
          { field: "tof_formats", as: "tof_formats", type: "string" },
        ],
        380,
      ),

      // 5. MOF creative generator AI
      aiNode(
        "5",
        "AI: Generate MOF consideration creatives",
        1000,
        "You are a creative director specializing in mid-funnel consideration ads. Generate 3 MOF creative concepts focused on product education, social proof, and feature highlights. Include: headline, body copy, visual description, CTA, ad format, and target platform. Respond JSON: {mof_creatives: string, mof_headline_1: string, mof_headline_2: string, mof_headline_3: string, mof_formats: string}",
        "Strategy: {mof_strategy}. Product: {product_description}. Audience: {persona_summary}. Theme: {overall_theme}. Tone: {brand_tone}.",
        [
          { field: "mof_creatives", as: "mof_creatives", type: "string" },
          { field: "mof_headline_1", as: "mof_headline_1", type: "string" },
          { field: "mof_headline_2", as: "mof_headline_2", type: "string" },
          { field: "mof_headline_3", as: "mof_headline_3", type: "string" },
          { field: "mof_formats", as: "mof_formats", type: "string" },
        ],
        380,
      ),

      // 6. BOF creative generator AI
      aiNode(
        "6",
        "AI: Generate BOF conversion creatives",
        1240,
        "You are a creative director specializing in bottom-funnel conversion ads. Generate 3 BOF creative concepts focused on urgency, specific offers, testimonials, and direct CTAs. Include: headline, body copy, visual description, CTA, ad format, and target platform. Respond JSON: {bof_creatives: string, bof_headline_1: string, bof_headline_2: string, bof_headline_3: string, bof_formats: string}",
        "Strategy: {bof_strategy}. Product: {product_description}. Audience: {persona_summary}. Theme: {overall_theme}. Tone: {brand_tone}. Campaign goal: {campaign_goal}.",
        [
          { field: "bof_creatives", as: "bof_creatives", type: "string" },
          { field: "bof_headline_1", as: "bof_headline_1", type: "string" },
          { field: "bof_headline_2", as: "bof_headline_2", type: "string" },
          { field: "bof_headline_3", as: "bof_headline_3", type: "string" },
          { field: "bof_formats", as: "bof_formats", type: "string" },
        ],
        380,
      ),

      // 7. Matrix document builder Sheets
      sheetsNode(
        "7",
        "Build creative matrix document (Sheets)",
        1480,
        "Creative_Matrix",
        '[["{campaign_name}","TOF","{tof_headline_1}","{tof_formats}","{budget_tof_pct}%","{tof_strategy}","=NOW()"],["","MOF","{mof_headline_1}","{mof_formats}","{budget_mof_pct}%","{mof_strategy}",""],["","BOF","{bof_headline_1}","{bof_formats}","{budget_bof_pct}%","{bof_strategy}",""]]',
      ),

      // 8. Review distribution email
      emailNode(
        "8",
        "Send creative matrix for review",
        1720,
        "{creative_review_team_email}",
        "Creative Matrix Ready: {campaign_name} - {overall_theme}",
        "<h2>Full-Funnel Creative Matrix</h2><p>Campaign: <strong>{campaign_name}</strong></p><p>Theme: {overall_theme}</p><p>Total Creatives: {total_creatives} across TOF/MOF/BOF</p><hr><h3>TOF Awareness ({budget_tof_pct}% budget)</h3><p>{tof_creatives}</p><h3>MOF Consideration ({budget_mof_pct}% budget)</h3><p>{mof_creatives}</p><h3>BOF Conversion ({budget_bof_pct}% budget)</h3><p>{bof_creatives}</p><hr><p>Full matrix available in Google Sheets. Please review and approve within 48 hours.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("3", "6"),
      e("4", "7"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1213. TESTIMONIAL COLLECTOR (8 nodes)
  // ===================================================================

  "Testimonial Collector": {
    summary:
      "Captures feedback form submissions via webhook, extracts feedback data with AI, analyzes sentiment and NPS, routes promoters vs detractors, sends a permission request email to promoters, processes the permission response, formats the testimonial in code, and saves to a marketing asset library in Sheets.",
    nodes: [
      trigger("Webhook: Feedback form submitted", "webhook_form", {
        source: "feedback_form",
        event: "form.submitted",
        howItStarts:
          "Fires when a customer submits a feedback or review form, initiating the testimonial collection pipeline that identifies promoters and requests permission to use their feedback as marketing testimonials",
        setup:
          "Configure feedback form webhook (Outgrow Forms, Outgrow Forms, or custom). Set up OpenAI API for sentiment analysis. Configure email sending. Set up Google Sheets for testimonial library.",
        inputFields: [],
      }),

      // 2. AI extract feedback data
      aiNode(
        "2",
        "AI: Extract feedback form data",
        280,
        "You are a data extraction specialist for customer feedback. Extract all relevant fields from this form submission regardless of format. Find the customer name, email, company name (if B2B), product or service rated, numeric rating or NPS score (1-10 scale), text feedback or review, date submitted, and any specific feature mentions. Respond JSON: {customer_name: string, customer_email: string, company_name: string, product_name: string, rating_score: number, feedback_text: string, submit_date: string, features_mentioned: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "rating_score", as: "rating_score", type: "number" },
          { field: "feedback_text", as: "feedback_text", type: "string" },
          { field: "submit_date", as: "submit_date", type: "string" },
          {
            field: "features_mentioned",
            as: "features_mentioned",
            type: "string",
          },
        ],
      ),

      // 3. AI feedback analyzer
      aiNode(
        "3",
        "AI: Analyze sentiment and testimonial quality",
        520,
        "You are a customer feedback analyst. Analyze this feedback for sentiment, testimonial potential, and marketing usefulness. Determine the NPS category (promoter 9-10, passive 7-8, detractor 0-6), extract quotable snippets, assess the authenticity and specificity of the feedback, and rate the testimonial quality from 1-10. Respond JSON: {nps_category: string, sentiment: string, quotable_snippet: string, testimonial_quality: number, marketing_usefulness: string, key_benefit_mentioned: string, suggested_use_case: string}",
        "Customer: {customer_name} from {company_name}. Rating: {rating_score}/10. Feedback: {feedback_text}. Features mentioned: {features_mentioned}.",
        [
          { field: "nps_category", as: "nps_category", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "quotable_snippet", as: "quotable_snippet", type: "string" },
          {
            field: "testimonial_quality",
            as: "testimonial_quality",
            type: "number",
          },
          {
            field: "marketing_usefulness",
            as: "marketing_usefulness",
            type: "string",
          },
          { field: "key_benefit_mentioned", as: "key_benefit", type: "string" },
          {
            field: "suggested_use_case",
            as: "suggested_use_case",
            type: "string",
          },
        ],
      ),

      // 4. Promoter/detractor router condition
      n("4", "condition", "Route by NPS category and quality", 760, {
        conditionData: [
          { variable: "nps_category", operator: "equals", value: "promoter" },
          {
            variable: "testimonial_quality",
            operator: "greater_than",
            value: "6",
          },
        ],
      }),

      // 5. Permission request sender email
      emailNode(
        "5",
        "Send testimonial permission request",
        1000,
        "{customer_email}",
        "Can we share your feedback, {customer_name}?",
        '<h2>Thank you for your kind words!</h2><p>Hi {customer_name},</p><p>We were thrilled to read your recent feedback about {product_name}. Your experience means a lot to us.</p><p>We would love to feature your testimonial in our marketing materials. Here is what we would like to share:</p><blockquote><em>"{quotable_snippet}"</em><br>-- {customer_name}, {company_name}</blockquote><p>Would you be comfortable with us using this? Simply reply to this email with your approval, or let us know if you would like any changes.</p><p>Thank you for being an amazing customer!</p>',
      ),

      // 6. Permission response processor condition
      n("6", "condition", "Check permission response status", 1240, {
        conditionData: [
          { variable: "permission_granted", operator: "equals", value: "true" },
        ],
      }),

      // 7. Testimonial formatter runCode
      n("7", "runCode", "Format testimonial for marketing use", 1480, {
        runCodeDetails: {
          code: `// Format the approved testimonial into multiple marketing-ready formats
const name = variables.customer_name || 'Customer';
const company = variables.company_name || '';
const quote = variables.quotable_snippet || variables.feedback_text || '';
const benefit = variables.key_benefit || '';
const rating = variables.rating_score || 0;
const attribution = company ? name + ', ' + company : name;
const short_quote = quote.length > 100 ? quote.substring(0, 97) + '...' : quote;
const social_format = '"' + short_quote + '" -- ' + attribution;
const website_format = '<div class="testimonial"><blockquote>' + quote + '</blockquote><cite>' + attribution + '</cite><div class="rating">' + rating + '/10</div></div>';
const case_study_hook = benefit ? 'How ' + attribution + ' achieved ' + benefit + ' with our product' : 'Why ' + attribution + ' recommends us';
const testimonial_id = 'TEST-' + Date.now().toString(36).toUpperCase();
return { testimonial_id: testimonial_id, attribution: attribution, short_quote: short_quote, social_format: social_format, website_html: website_format, case_study_hook: case_study_hook };`,
          outputs: [
            { name: "testimonial_id", type: "string" },
            { name: "attribution", type: "string" },
            { name: "short_quote", type: "string" },
            { name: "social_format", type: "string" },
            { name: "website_html", type: "string" },
            { name: "case_study_hook", type: "string" },
          ],
        },
      }),

      // 8. Marketing asset library Sheets
      sheetsNode(
        "8",
        "Save to testimonial library (Sheets)",
        1720,
        "Testimonial_Library",
        '[["{testimonial_id}","{customer_name}","{company_name}","{product_name}","{rating_score}","{quotable_snippet}","{attribution}","{nps_category}","{testimonial_quality}","{suggested_use_case}","{social_format}","{case_study_hook}","approved","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1214. TESTIMONIAL COMPILATION VIDEO (8 nodes)
  // ===================================================================

  "Testimonial Compilation Video": {
    summary:
      "Runs on a schedule to batch-select approved testimonials from Sheets, uses AI to generate a compilation video script, matches visual assets via code, renders the video with Kling AI, checks render quality, sends for human approval, and publishes to TikTok.",
    nodes: [
      trigger("Schedule: Weekly testimonial video compilation", "schedule", {
        schedule: "0 10 * * 5",
        howItStarts:
          "Fires every Friday at 10 AM to compile the week's best approved testimonials into a social video for TikTok, handling script generation, video rendering, quality checks, and publishing",
        setup:
          "Set up Google Sheets with approved testimonials. Configure Kling AI API for video rendering. Provide TikTok Business API credentials. Configure approval workflow contacts.",
        inputFields: [],
      }),

      // 2. Sheets testimonial batch selector
      n("2", "hitApi", "Fetch approved testimonials batch (Sheets)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Testimonial_Library!A2:N100?majorDimension=ROWS",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "testimonials_raw", type: "string" },
            {
              name: "values.length",
              customName: "testimonial_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI compilation script generator
      aiNode(
        "3",
        "AI: Generate compilation video script",
        520,
        "You are a social media video producer. Given a batch of customer testimonials, create a compelling 30-60 second TikTok video script that compiles the best quotes into a dynamic testimonial montage. Include: intro hook (2 seconds), testimonial segments with name cards, transitions between quotes, background music suggestions, and a closing CTA. Respond JSON: {video_script: string, intro_hook: string, testimonial_segments: string, closing_cta: string, music_suggestion: string, estimated_duration_seconds: number, visual_style: string}",
        "Testimonials batch ({testimonial_count} total): {testimonials_raw}",
        [
          { field: "video_script", as: "video_script", type: "string" },
          { field: "intro_hook", as: "intro_hook", type: "string" },
          {
            field: "testimonial_segments",
            as: "testimonial_segments",
            type: "string",
          },
          { field: "closing_cta", as: "closing_cta", type: "string" },
          { field: "music_suggestion", as: "music_suggestion", type: "string" },
          {
            field: "estimated_duration_seconds",
            as: "video_duration",
            type: "number",
          },
          { field: "visual_style", as: "visual_style", type: "string" },
        ],
      ),

      // 4. Visual asset matcher runCode
      n("4", "runCode", "Match visual assets to testimonial segments", 760, {
        runCodeDetails: {
          code: `// Parse testimonial segments and assign visual treatments
const segments = JSON.parse(variables.testimonial_segments || '[]');
const style = variables.visual_style || 'modern-minimal';
const assetMap = [];
const bgColors = ['#1a1a2e', '#16213e', '#0f3460', '#533483', '#e94560'];
if (Array.isArray(segments)) {
  segments.forEach((seg, i) => {
    assetMap.push({
      segment_index: i,
      quote: seg.quote || seg.text || seg,
      name_card: seg.name || 'Customer ' + (i + 1),
      bg_color: bgColors[i % bgColors.length],
      text_animation: i % 2 === 0 ? 'fade-in' : 'slide-up',
      duration_ms: 4000,
    });
  });
}
const kling_prompt = 'Create a ' + style + ' testimonial compilation video with text overlays on colored backgrounds. ' + variables.intro_hook + ' Then show ' + assetMap.length + ' customer quotes with name cards. End with: ' + variables.closing_cta;
return { asset_map: JSON.stringify(assetMap), segment_count: assetMap.length, kling_video_prompt: kling_prompt };`,
          outputs: [
            { name: "asset_map", type: "string" },
            { name: "segment_count", type: "number" },
            { name: "kling_video_prompt", type: "string" },
          ],
        },
      }),

      // 5. Kling video renderer API
      n("5", "hitApi", "Render testimonial video (Kling AI)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "prompt", value: "{kling_video_prompt}" },
            { key: "duration", value: "10" },
            { key: "aspect_ratio", value: "9:16" },
            { key: "mode", value: "high_quality" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "render_task_id",
              type: "string",
            },
            {
              name: "data.task_status",
              customName: "render_status",
              type: "string",
            },
            {
              name: "data.task_result.videos[0].url",
              customName: "rendered_video_url",
              type: "string",
            },
            {
              name: "data.task_result.videos[0].duration",
              customName: "actual_duration",
              type: "string",
            },
          ],
        },
      }),

      // 6. Render quality check condition
      n("6", "condition", "Verify render quality and status", 1240, {
        conditionData: [
          { variable: "render_status", operator: "equals", value: "completed" },
        ],
      }),

      // 7. Human approval gate
      n("7", "approvalGate", "Review compiled testimonial video", 1480, {
        approvalPrompt:
          "Testimonial compilation video is ready for review.\n\nScript: {video_script}\nDuration: {video_duration}s\nSegments: {segment_count} testimonials\nVideo URL: {rendered_video_url}\n\nPlease review the video for brand consistency, testimonial accuracy, and production quality before publishing to TikTok.",
      }),

      // 8. TikTok publisher API
      n("8", "hitApi", "Publish to TikTok", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://open.tiktokapis.com/v2/post/publish/video/init/",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "post_info",
              value:
                '{"title":"Our customers say it best #{product_hashtag} #testimonials #customerlove","privacy_level":"PUBLIC_TO_EVERYONE","disable_duet":false,"disable_comment":false,"disable_stitch":false}',
            },
            {
              key: "source_info",
              value:
                '{"source":"PULL_FROM_URL","video_url":"{rendered_video_url}"}',
            },
          ],
          variables: [
            {
              name: "data.publish_id",
              customName: "tiktok_publish_id",
              type: "string",
            },
            {
              name: "data.upload_url",
              customName: "tiktok_upload_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1215. TIER-UP CONGRATULATIONS (7 nodes)
  // ===================================================================

  "Tier-Up Congratulations": {
    summary:
      "Captures loyalty tier upgrade events via webhook, extracts tier data with AI, fetches new tier benefits from the loyalty API, enriches member history from HubSpot, composes personalized congratulations with AI, sends a multi-channel celebration email, and logs the tier change in CRM.",
    nodes: [
      trigger("Webhook: Loyalty tier upgrade event", "webhook_app", {
        source: "loyalty_platform",
        event: "member.tier.upgraded",
        howItStarts:
          "Fires when a customer achieves a new loyalty tier, initiating a personalized congratulations campaign across email with CRM tracking to celebrate the achievement and reinforce tier benefits",
        setup:
          "Configure loyalty platform webhook for tier upgrade events. Set up loyalty API access for benefits lookup. Provide HubSpot API token. Configure email sending domain.",
        inputFields: [],
      }),

      // 2. AI extract tier data
      aiNode(
        "2",
        "AI: Extract tier upgrade details",
        280,
        "You are a data extraction specialist for loyalty programs. Extract tier upgrade details from this webhook payload regardless of field format. Find the member email, member name, previous tier name, new tier name, points balance, points needed for upgrade, upgrade date, and member ID. Respond JSON: {member_email: string, member_name: string, previous_tier: string, new_tier: string, points_balance: number, points_for_upgrade: number, upgrade_date: string, member_id: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_name", as: "member_name", type: "string" },
          { field: "previous_tier", as: "previous_tier", type: "string" },
          { field: "new_tier", as: "new_tier", type: "string" },
          { field: "points_balance", as: "points_balance", type: "number" },
          {
            field: "points_for_upgrade",
            as: "points_for_upgrade",
            type: "number",
          },
          { field: "upgrade_date", as: "upgrade_date", type: "string" },
          { field: "member_id", as: "member_id", type: "string" },
        ],
      ),

      // 3. New tier benefits fetcher API
      n("3", "hitApi", "Fetch new tier benefits", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.loyaltyplatform.com/v1/tiers/{new_tier}/benefits",
          headers: [
            { key: "Authorization", value: "Bearer {loyalty_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "tier.benefits",
              customName: "tier_benefits_list",
              type: "string",
            },
            {
              name: "tier.exclusive_perks",
              customName: "exclusive_perks",
              type: "string",
            },
            {
              name: "tier.discount_rate",
              customName: "tier_discount_rate",
              type: "number",
            },
            {
              name: "tier.points_multiplier",
              customName: "points_multiplier",
              type: "number",
            },
            {
              name: "tier.badge_image_url",
              customName: "tier_badge_url",
              type: "string",
            },
          ],
        },
      }),

      // 4. Member history enricher HubSpot
      hubspotGetNode(
        "4",
        "Enrich member history (HubSpot)",
        760,
        '/crm/v3/objects/contacts/search?filterGroups=[{"filters":[{"propertyName":"email","operator":"EQ","value":"{member_email}"}]}]&properties=firstname,lastname,total_revenue,hs_object_id,num_associated_deals,loyalty_join_date',
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "lifetime_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "total_purchases",
            type: "number",
          },
          {
            name: "results[0].properties.loyalty_join_date",
            customName: "loyalty_join_date",
            type: "string",
          },
        ],
      ),

      // 5. AI congratulations composer
      aiNode(
        "5",
        "AI: Compose tier-up congratulations",
        1000,
        "You are a loyalty marketing specialist. Write an enthusiastic, personalized congratulations message for a customer who just earned a new loyalty tier. Highlight their journey (previous tier to new tier), showcase the new benefits they have unlocked, include their points balance, and make them feel valued. Create both an email version and a short SMS version. Respond JSON: {email_subject: string, email_body_html: string, sms_message: string, celebration_headline: string}",
        "Member: {member_name} ({member_email}). Upgraded from {previous_tier} to {new_tier}. Points: {points_balance}. Lifetime revenue: {lifetime_revenue}. Total purchases: {total_purchases}. Member since: {loyalty_join_date}. New benefits: {tier_benefits_list}. Exclusive perks: {exclusive_perks}. New discount rate: {tier_discount_rate}%. Points multiplier: {points_multiplier}x.",
        [
          { field: "email_subject", as: "congrats_subject", type: "string" },
          { field: "email_body_html", as: "congrats_body", type: "string" },
          { field: "sms_message", as: "congrats_sms", type: "string" },
          {
            field: "celebration_headline",
            as: "celebration_headline",
            type: "string",
          },
        ],
      ),

      // 6. Multi-channel celebration email sender
      emailNode(
        "6",
        "Send tier-up celebration email",
        1240,
        "{member_email}",
        "{congrats_subject}",
        "{congrats_body}",
      ),

      // 7. CRM loyalty logger HubSpot
      hubspotPostNode(
        "7",
        "Update CRM loyalty tier (HubSpot)",
        1480,
        "/crm/v3/objects/contacts/{hubspot_contact_id}",
        [
          {
            key: "properties",
            value:
              '{"loyalty_tier":"{new_tier}","loyalty_previous_tier":"{previous_tier}","loyalty_tier_upgrade_date":"{upgrade_date}","loyalty_points_balance":"{points_balance}","loyalty_discount_rate":"{tier_discount_rate}","loyalty_points_multiplier":"{points_multiplier}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_updated_id", type: "string" },
          {
            name: "properties.loyalty_tier",
            customName: "confirmed_tier",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1216. TIKTOK CONTENT CALENDAR POST (8 nodes)
  // ===================================================================

  "TikTok Content Calendar Post": {
    summary:
      "Triggers when a content calendar entry becomes due via webhook, extracts the content brief with AI, researches current trends, drafts the TikTok post with AI, runs a compliance check, sends for human approval, publishes to TikTok, and updates the content calendar in Sheets.",
    nodes: [
      trigger("Webhook: Content calendar entry due", "webhook_app", {
        source: "content_calendar",
        event: "entry.due",
        howItStarts:
          "Fires when a scheduled content calendar entry reaches its publish date, initiating trend research, content creation, compliance review, and TikTok publishing",
        setup:
          "Configure content calendar webhook (Notion, Airtable, or Sheets automation). Set up TikTok Business API. Provide OpenAI API key. Configure approval contacts.",
        inputFields: [],
      }),

      // 2. AI extract content brief
      aiNode(
        "2",
        "AI: Extract content calendar brief",
        280,
        "You are a data extraction specialist for content management. Extract the content brief from this calendar webhook regardless of format. Find the content title, topic or theme, target audience, content type (educational/entertaining/promotional), key message, brand hashtags, and scheduled publish time. Respond JSON: {content_title: string, topic: string, target_audience: string, content_type: string, key_message: string, brand_hashtags: string, publish_time: string, calendar_entry_id: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "content_title", as: "content_title", type: "string" },
          { field: "topic", as: "content_topic", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "key_message", as: "key_message", type: "string" },
          { field: "brand_hashtags", as: "brand_hashtags", type: "string" },
          { field: "publish_time", as: "publish_time", type: "string" },
          {
            field: "calendar_entry_id",
            as: "calendar_entry_id",
            type: "string",
          },
        ],
      ),

      // 3. Trend research API
      n("3", "hitApi", "Research current TikTok trends", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://open.tiktokapis.com/v2/research/hashtag/detail/?fields=id,name,view_count&hashtag_name={content_topic}",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data.id", customName: "hashtag_id", type: "string" },
            {
              name: "data.name",
              customName: "trending_hashtag",
              type: "string",
            },
            {
              name: "data.view_count",
              customName: "hashtag_views",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI post drafter
      aiNode(
        "4",
        "AI: Draft TikTok post content",
        760,
        "You are a TikTok content creator specializing in viral content. Given the content brief and current trend data, create a TikTok post including: video script (hook in first 2 seconds, value delivery, CTA), caption with hashtags, and posting notes. The content should feel native to TikTok and appropriate for the content type specified. Respond JSON: {video_script: string, hook_line: string, caption: string, hashtags: string, posting_notes: string, estimated_engagement: string, content_warning_flags: string}",
        "Title: {content_title}. Topic: {content_topic}. Type: {content_type}. Audience: {target_audience}. Key message: {key_message}. Brand hashtags: {brand_hashtags}. Trending: #{trending_hashtag} ({hashtag_views} views).",
        [
          { field: "video_script", as: "tiktok_script", type: "string" },
          { field: "hook_line", as: "hook_line", type: "string" },
          { field: "caption", as: "tiktok_caption", type: "string" },
          { field: "hashtags", as: "final_hashtags", type: "string" },
          { field: "posting_notes", as: "posting_notes", type: "string" },
          {
            field: "estimated_engagement",
            as: "estimated_engagement",
            type: "string",
          },
          {
            field: "content_warning_flags",
            as: "warning_flags",
            type: "string",
          },
        ],
      ),

      // 5. Content compliance check condition
      n("5", "condition", "Check content compliance", 1000, {
        conditionData: [
          { variable: "warning_flags", operator: "equals", value: "none" },
        ],
      }),

      // 6. Human approval gate
      n("6", "approvalGate", "Approve TikTok post for publishing", 1240, {
        approvalPrompt:
          "TikTok post ready for review:\n\nTitle: {content_title}\nType: {content_type}\n\nHook: {hook_line}\nScript: {tiktok_script}\nCaption: {tiktok_caption}\nHashtags: {final_hashtags}\n\nWarning flags: {warning_flags}\nEstimated engagement: {estimated_engagement}\n\nApprove to publish to TikTok.",
      }),

      // 7. TikTok publisher API
      n("7", "hitApi", "Publish post to TikTok", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://open.tiktokapis.com/v2/post/publish/content/init/",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "post_info",
              value:
                '{"title":"{tiktok_caption} {final_hashtags}","privacy_level":"PUBLIC_TO_EVERYONE","disable_duet":false,"disable_comment":false}',
            },
            {
              key: "source_info",
              value:
                '{"source":"PULL_FROM_URL","video_url":"{video_asset_url}"}',
            },
          ],
          variables: [
            {
              name: "data.publish_id",
              customName: "publish_id",
              type: "string",
            },
            {
              name: "data.status",
              customName: "publish_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Content calendar updater Sheets
      sheetsNode(
        "8",
        "Update content calendar status (Sheets)",
        1720,
        "Content_Calendar",
        '[["{calendar_entry_id}","{content_title}","{content_type}","published","{publish_id}","{final_hashtags}","{estimated_engagement}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1217. TIKTOK DIRECT PUBLISH (9 nodes)
  // ===================================================================

  "TikTok Direct Publish": {
    summary:
      "Takes a manual video brief, generates a script with AI, renders the video via Kling AI, validates the render, handles failures via Slack, sends for human approval, finalizes captions and hashtags with AI, publishes directly to TikTok, and logs the result in Sheets.",
    nodes: [
      trigger("Manual: Submit TikTok video brief", "manual", {
        howItStarts:
          "Manually triggered when a team member submits a video brief for direct TikTok publishing, initiating script generation, AI video rendering, review, and publishing",
        setup:
          "Configure OpenAI API key for script generation. Set up Kling AI API for video rendering. Provide TikTok Business API credentials. Configure Slack webhook for failure alerts.",
        inputFields: [
          {
            name: "video_topic",
            type: "string",
            example: "5 productivity hacks for remote workers",
          },
          {
            name: "video_style",
            type: "string",
            example: "fast-paced with text overlays",
          },
          {
            name: "target_audience",
            type: "string",
            example: "Gen Z and Millennial professionals",
          },
          {
            name: "brand_voice",
            type: "string",
            example: "casual, energetic, helpful",
          },
          { name: "video_length", type: "string", example: "30-60 seconds" },
        ],
      }),

      // 2. AI script generator
      aiNode(
        "2",
        "AI: Generate TikTok video script",
        280,
        "You are an expert TikTok scriptwriter. Create a complete video script with a strong hook in the first 2 seconds, engaging body content, and a clear CTA. Include timing cues, text overlay instructions, visual directions, and transition notes. The script should match the specified style and length. Respond JSON: {full_script: string, hook_text: string, body_segments: string, cta_text: string, text_overlays: string, visual_directions: string, estimated_seconds: number}",
        "Topic: {video_topic}. Style: {video_style}. Audience: {target_audience}. Voice: {brand_voice}. Target length: {video_length}.",
        [
          { field: "full_script", as: "full_script", type: "string" },
          { field: "hook_text", as: "hook_text", type: "string" },
          { field: "body_segments", as: "body_segments", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
          { field: "text_overlays", as: "text_overlays", type: "string" },
          {
            field: "visual_directions",
            as: "visual_directions",
            type: "string",
          },
          {
            field: "estimated_seconds",
            as: "estimated_seconds",
            type: "number",
          },
        ],
      ),

      // 3. Kling video renderer API
      n("3", "hitApi", "Render video with Kling AI", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "prompt",
              value:
                "{visual_directions}. Style: {video_style}. Hook: {hook_text}. Script: {full_script}",
            },
            { key: "duration", value: "10" },
            { key: "aspect_ratio", value: "9:16" },
            { key: "mode", value: "high_quality" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "render_task_id",
              type: "string",
            },
            {
              name: "data.task_status",
              customName: "render_status",
              type: "string",
            },
            {
              name: "data.task_result.videos[0].url",
              customName: "video_url",
              type: "string",
            },
            {
              name: "data.task_result.videos[0].duration",
              customName: "actual_duration",
              type: "string",
            },
          ],
        },
      }),

      // 4. Render validation condition
      n("4", "condition", "Validate render completed successfully", 760, {
        conditionData: [
          { variable: "render_status", operator: "equals", value: "completed" },
        ],
      }),

      // 5. Render failure handler Slack
      slackNode(
        "5",
        "Alert team of render failure (Slack)",
        1000,
        ":x: *TikTok Video Render Failed*\nTopic: {video_topic}\nTask ID: {render_task_id}\nStatus: {render_status}\nScript: {hook_text}...\n\nPlease investigate and re-trigger the workflow.",
        380,
      ),

      // 6. Human approval gate
      n("6", "approvalGate", "Review rendered video for publishing", 1000, {
        approvalPrompt:
          "TikTok video render complete.\n\nTopic: {video_topic}\nStyle: {video_style}\nDuration: {actual_duration}s\nVideo URL: {video_url}\n\nScript:\n{full_script}\n\nText overlays: {text_overlays}\n\nApprove to proceed with caption finalization and TikTok publishing.",
      }),

      // 7. Caption/hashtag finalizer AI
      aiNode(
        "7",
        "AI: Finalize caption and hashtags",
        1240,
        "You are a TikTok growth expert. Given the approved video script and topic, create an optimized caption and hashtag set designed for maximum reach. Include trending hashtags relevant to the topic, niche hashtags for targeted reach, and branded hashtags. Keep the caption engaging and under 150 characters. Respond JSON: {final_caption: string, hashtag_set: string, alt_text: string, best_post_time: string}",
        "Topic: {video_topic}. Script hook: {hook_text}. CTA: {cta_text}. Audience: {target_audience}. Voice: {brand_voice}.",
        [
          { field: "final_caption", as: "final_caption", type: "string" },
          { field: "hashtag_set", as: "final_hashtags", type: "string" },
          { field: "alt_text", as: "video_alt_text", type: "string" },
          { field: "best_post_time", as: "best_post_time", type: "string" },
        ],
      ),

      // 8. TikTok direct publisher API
      n("8", "hitApi", "Publish video to TikTok", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://open.tiktokapis.com/v2/post/publish/video/init/",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "post_info",
              value:
                '{"title":"{final_caption} {final_hashtags}","privacy_level":"PUBLIC_TO_EVERYONE","disable_duet":false,"disable_comment":false,"disable_stitch":false,"video_cover_timestamp_ms":1000}',
            },
            {
              key: "source_info",
              value: '{"source":"PULL_FROM_URL","video_url":"{video_url}"}',
            },
          ],
          variables: [
            {
              name: "data.publish_id",
              customName: "tiktok_publish_id",
              type: "string",
            },
            {
              name: "data.status",
              customName: "tiktok_publish_status",
              type: "string",
            },
          ],
        },
      }),

      // 9. Post-publish monitor Sheets
      sheetsNode(
        "9",
        "Log publish result (Sheets)",
        1720,
        "TikTok_Publish_Log",
        '[["{video_topic}","{video_style}","{hook_text}","{final_caption}","{final_hashtags}","{tiktok_publish_id}","{tiktok_publish_status}","{actual_duration}","{best_post_time}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_2"),
      e("4", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1218. TIKTOK DRAFT VIDEO POST (8 nodes)
  // ===================================================================

  "TikTok Draft Video Post": {
    summary:
      "Takes a manual draft request, generates script and caption with AI, renders the video via Kling AI, checks render quality, refines the caption with AI, sends for human review, publishes as a TikTok draft, and notifies the team on Slack.",
    nodes: [
      trigger("Manual: Create TikTok video draft", "manual", {
        howItStarts:
          "Manually triggered when a creator wants to prepare a TikTok video draft for later review and scheduling, generating script, rendering video, and saving as a draft",
        setup:
          "Configure OpenAI API key for content generation. Set up Kling AI API for video rendering. Provide TikTok Business API credentials for draft creation. Configure Slack webhook for notifications.",
        inputFields: [
          {
            name: "draft_topic",
            type: "string",
            example: "Behind the scenes of our product launch",
          },
          {
            name: "content_angle",
            type: "string",
            example: "Day-in-the-life format showing real team moments",
          },
          {
            name: "target_persona",
            type: "string",
            example: "Startup founders interested in product development",
          },
          {
            name: "desired_tone",
            type: "string",
            example: "authentic, relatable, inspiring",
          },
        ],
      }),

      // 2. AI script and caption generator
      aiNode(
        "2",
        "AI: Generate script and initial caption",
        280,
        "You are a TikTok content strategist. Create both a video script and initial caption for a TikTok draft. The script should have a compelling hook, clear narrative arc, and strong ending. The caption should complement the video without repeating it. Include text overlay suggestions and music mood. Respond JSON: {video_script: string, hook: string, narrative_arc: string, initial_caption: string, text_overlays: string, music_mood: string, visual_prompt: string}",
        "Topic: {draft_topic}. Angle: {content_angle}. Persona: {target_persona}. Tone: {desired_tone}.",
        [
          { field: "video_script", as: "draft_script", type: "string" },
          { field: "hook", as: "script_hook", type: "string" },
          { field: "narrative_arc", as: "narrative_arc", type: "string" },
          { field: "initial_caption", as: "initial_caption", type: "string" },
          { field: "text_overlays", as: "text_overlays", type: "string" },
          { field: "music_mood", as: "music_mood", type: "string" },
          { field: "visual_prompt", as: "visual_prompt", type: "string" },
        ],
      ),

      // 3. Kling video renderer API
      n("3", "hitApi", "Render draft video (Kling AI)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "prompt",
              value:
                "{visual_prompt}. Tone: {desired_tone}. Hook scene: {script_hook}. Style: {content_angle}",
            },
            { key: "duration", value: "10" },
            { key: "aspect_ratio", value: "9:16" },
            { key: "mode", value: "high_quality" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "render_task_id",
              type: "string",
            },
            {
              name: "data.task_status",
              customName: "render_status",
              type: "string",
            },
            {
              name: "data.task_result.videos[0].url",
              customName: "draft_video_url",
              type: "string",
            },
            {
              name: "data.task_result.videos[0].duration",
              customName: "video_duration",
              type: "string",
            },
          ],
        },
      }),

      // 4. Render quality check condition
      n("4", "condition", "Verify draft render quality", 760, {
        conditionData: [
          { variable: "render_status", operator: "equals", value: "completed" },
        ],
      }),

      // 5. AI caption refinement
      aiNode(
        "5",
        "AI: Refine caption for engagement",
        1000,
        "You are a TikTok copywriting expert. Refine the initial caption based on the rendered video context. Optimize for engagement by adding a question or CTA, trending hashtags, and ensuring the caption complements the visual hook. Keep it concise and scroll-stopping. Respond JSON: {refined_caption: string, hashtags: string, engagement_hook: string, posting_recommendation: string}",
        "Topic: {draft_topic}. Initial caption: {initial_caption}. Script hook: {script_hook}. Target persona: {target_persona}. Tone: {desired_tone}. Video duration: {video_duration}s.",
        [
          { field: "refined_caption", as: "refined_caption", type: "string" },
          { field: "hashtags", as: "draft_hashtags", type: "string" },
          { field: "engagement_hook", as: "engagement_hook", type: "string" },
          {
            field: "posting_recommendation",
            as: "posting_recommendation",
            type: "string",
          },
        ],
      ),

      // 6. Human review approval gate
      n("6", "approvalGate", "Review draft video before saving", 1240, {
        approvalPrompt:
          "TikTok draft video ready for review:\n\nTopic: {draft_topic}\nAngle: {content_angle}\n\nScript: {draft_script}\nCaption: {refined_caption}\nHashtags: {draft_hashtags}\nVideo URL: {draft_video_url}\nDuration: {video_duration}s\n\nPosting recommendation: {posting_recommendation}\n\nApprove to save as TikTok draft.",
      }),

      // 7. TikTok draft publisher API
      n("7", "hitApi", "Save as TikTok draft", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://open.tiktokapis.com/v2/post/publish/video/init/",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "post_info",
              value:
                '{"title":"{refined_caption} {draft_hashtags}","privacy_level":"SELF_ONLY","disable_duet":false,"disable_comment":false}',
            },
            {
              key: "source_info",
              value:
                '{"source":"PULL_FROM_URL","video_url":"{draft_video_url}"}',
            },
          ],
          variables: [
            {
              name: "data.publish_id",
              customName: "draft_publish_id",
              type: "string",
            },
            {
              name: "data.status",
              customName: "draft_save_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Draft status notifier Slack
      slackNode(
        "8",
        "Notify team of saved draft (Slack)",
        1720,
        ":pencil: *TikTok Draft Saved*\nTopic: {draft_topic}\nAngle: {content_angle}\nCaption: {refined_caption}\nHashtags: {draft_hashtags}\nDraft ID: {draft_publish_id}\nDuration: {video_duration}s\nRecommended posting: {posting_recommendation}\n\nDraft is ready for final review and scheduling in TikTok Creator Studio.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1219. TRIAL SIGNUP B2B ONBOARDING (10 nodes)
  // ===================================================================

  "Trial Signup B2B Onboarding": {
    summary:
      "Captures B2B trial signups via webhook, extracts company data with AI, enriches with Clearbit, sends a welcome email, waits 1 day, checks team setup status, waits 2 more days, sends an integration guide, waits 4 more days, and triggers a sales assist alert on Slack.",
    nodes: [
      trigger("Webhook: B2B trial signup received", "webhook_app", {
        source: "app_signup",
        event: "trial.created",
        howItStarts:
          "Fires when a new B2B trial signup is received, initiating a 7-day automated onboarding sequence with company enrichment, milestone checks, and sales handoff triggers",
        setup:
          "Configure application webhook for trial signup events. Set up Clearbit API for company enrichment. Configure email sending domain. Provide Slack webhook for sales alerts.",
        inputFields: [],
      }),

      // 2. AI extract company data
      aiNode(
        "2",
        "AI: Extract trial signup details",
        280,
        "You are a data extraction specialist for B2B SaaS signups. Extract trial signup details from this webhook regardless of field format. Find the user email, user name, company name, company domain, job title or role, team size estimate, use case mentioned, signup source or UTM parameters, and trial plan selected. Respond JSON: {user_email: string, user_name: string, company_name: string, company_domain: string, job_title: string, team_size: string, use_case: string, signup_source: string, trial_plan: string, account_id: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "team_size", as: "team_size", type: "string" },
          { field: "use_case", as: "use_case", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "trial_plan", as: "trial_plan", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
        ],
      ),

      // 3. Company enrichment Clearbit API
      n("3", "hitApi", "Enrich company data (Clearbit)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "string",
            },
            { name: "geo.city", customName: "company_city", type: "string" },
            { name: "tech", customName: "tech_stack", type: "string" },
          ],
        },
      }),

      // 4. Welcome setup email
      emailNode(
        "4",
        "Send welcome and setup email",
        760,
        "{user_email}",
        "Welcome to your trial, {user_name}! Let us get {company_name} set up",
        "<h2>Welcome aboard, {user_name}!</h2><p>We are excited to have {company_name} on board. Your {trial_plan} trial is now active.</p><h3>Quick Start Guide (3 steps):</h3><ol><li><strong>Invite your team</strong> -- Collaboration is key. Add your team members from Settings > Team.</li><li><strong>Connect your tools</strong> -- We integrate with the tools your team already uses. Check Settings > Integrations.</li><li><strong>Create your first project</strong> -- Use our templates to get started in under 5 minutes.</li></ol><p>Need help? Reply to this email or check out our <a href='{help_center_url}'>Help Center</a>.</p><p>Best,<br>The Onboarding Team</p>",
      ),

      // 5. Delay 1 day
      n("5", "delayAction", "Wait 1 day", 1000, {
        delayDetails: { duration: 1, unit: "days" },
      }),

      // 6. Day 1 team setup check condition
      n("6", "condition", "Check if team members invited", 1240, {
        conditionData: [
          {
            variable: "team_members_invited",
            operator: "greater_than",
            value: "0",
          },
        ],
      }),

      // 7. Delay 2 days
      n("7", "delayAction", "Wait 2 more days", 1480, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 8. Day 3 integration guide email
      emailNode(
        "8",
        "Send integration setup guide",
        1720,
        "{user_email}",
        "Connect {company_name}'s favorite tools -- {user_name}",
        "<h2>Unlock the full power of your trial</h2><p>Hi {user_name},</p><p>Teams in the {industry} industry that connect integrations in the first week see 3x more value from their trial.</p><h3>Popular Integrations for {industry} teams:</h3><ul><li><strong>Slack</strong> -- Get real-time notifications</li><li><strong>Google Workspace</strong> -- Sync docs and calendars</li><li><strong>Your CRM</strong> -- Keep customer data in sync</li></ul><p>As a {employee_count}-person team, you will benefit most from our team collaboration features. <a href='{integrations_url}?account={account_id}'>Set up integrations now</a>.</p><p>If you have questions, our team is here to help!</p>",
      ),

      // 9. Delay 4 days
      n("9", "delayAction", "Wait 4 more days", 1960, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 10. Sales assist trigger Slack
      slackNode(
        "10",
        "Trigger sales assist alert (Slack)",
        2200,
        ":briefcase: *Trial Week 1 Complete -- Sales Assist Needed*\nAccount: {company_name} ({company_domain})\nContact: {user_name} ({user_email})\nTitle: {job_title}\nPlan: {trial_plan}\n\nCompany Info:\n- Industry: {industry}\n- Employees: {employee_count}\n- Revenue: {annual_revenue}\n- City: {company_city}\n- Use case: {use_case}\n- Signup source: {signup_source}\n\nPlease review trial activity and reach out for a personalized demo or conversion conversation.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1220. TRIAL SIGNUP NO FIRST LOGIN (8 nodes)
  // ===================================================================

  "Trial Signup No First Login": {
    summary:
      "Scans HubSpot on schedule for trial signups with no first login, enriches their signup data in code, sends a quick-start rescue email, waits 1 day, sends a support offer email, checks if they qualify for sales escalation, and alerts the team on Slack.",
    nodes: [
      trigger("Schedule: Detect trial signups with no login", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Fires daily at 9 AM to identify trial users who signed up but never logged in, initiating a rescue sequence with helpful emails and eventual sales/success team escalation",
        setup:
          "Configure HubSpot API token with contact read access. Set up email sending domain. Configure Slack webhook for sales/success alerts. Define the no-login detection threshold (default: 24 hours).",
        inputFields: [],
      }),

      // 2. HubSpot no-login detector
      hubspotPostNode(
        "2",
        "Search for no-login trial users (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"subscriber"},{"propertyName":"hs_analytics_last_visit_timestamp","operator":"NOT_HAS_PROPERTY"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","createdate","hs_analytics_last_visit_timestamp","trial_plan","company","jobtitle"]',
          },
          { key: "limit", value: "50" },
        ],
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "user_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "user_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "user_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.createdate",
            customName: "signup_date",
            type: "string",
          },
          {
            name: "results[0].properties.trial_plan",
            customName: "trial_plan",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          { name: "total", customName: "no_login_count", type: "number" },
        ],
      ),

      // 3. Signup data enricher runCode
      n("3", "runCode", "Enrich and analyze signup data", 520, {
        runCodeDetails: {
          code: `// Calculate time since signup and determine urgency
const signupDate = new Date(variables.signup_date);
const now = new Date();
const hoursSinceSignup = Math.round((now - signupDate) / (1000 * 60 * 60));
const daysSinceSignup = Math.round(hoursSinceSignup / 24);
const urgency = daysSinceSignup >= 3 ? 'critical' : daysSinceSignup >= 2 ? 'high' : 'medium';
const jobTitle = (variables.job_title || '').toLowerCase();
const isEnterprise = (variables.company_name || '').length > 0 && (jobTitle.includes('director') || jobTitle.includes('vp') || jobTitle.includes('head') || jobTitle.includes('chief'));
const rescueApproach = isEnterprise ? 'white_glove' : 'self_serve';
return { hours_since_signup: hoursSinceSignup, days_since_signup: daysSinceSignup, urgency_level: urgency, is_enterprise_lead: isEnterprise, rescue_approach: rescueApproach };`,
          outputs: [
            { name: "hours_since_signup", type: "number" },
            { name: "days_since_signup", type: "number" },
            { name: "urgency_level", type: "string" },
            { name: "is_enterprise_lead", type: "boolean" },
            { name: "rescue_approach", type: "string" },
          ],
        },
      }),

      // 4. Rescue email 1: quick start
      emailNode(
        "4",
        "Send quick-start rescue email",
        760,
        "{user_email}",
        "Your trial is waiting, {user_first_name} -- log in now in 30 seconds",
        "<h2>We noticed you have not logged in yet</h2><p>Hi {user_first_name},</p><p>You signed up for your {trial_plan} trial {days_since_signup} day(s) ago, but we have not seen you log in yet. No worries -- getting started is easy!</p><h3>Log in and get set up in 30 seconds:</h3><ol><li>Click the button below to access your account</li><li>Follow the 3-step setup wizard</li><li>Start seeing results immediately</li></ol><p><a href='{app_login_url}' style='display:inline-block;background:#4F46E5;color:white;padding:12px 24px;border-radius:6px;text-decoration:none;font-weight:bold'>Log In Now</a></p><p>Need help? Just reply to this email -- a real person will respond within the hour.</p>",
      ),

      // 5. Delay 1 day
      n("5", "delayAction", "Wait 1 day", 1000, {
        delayDetails: { duration: 1, unit: "days" },
      }),

      // 6. Rescue email 2: support offer
      emailNode(
        "6",
        "Send support offer rescue email",
        1240,
        "{user_email}",
        "Need help getting started, {user_first_name}? We are here for you",
        "<h2>Let us help you get started</h2><p>Hi {user_first_name},</p><p>We know getting started with new tools can sometimes be overwhelming. That is why we want to offer you some extra help.</p><h3>Choose your preferred way to get started:</h3><ul><li><strong>Self-guided</strong>: <a href='{quickstart_guide_url}'>Follow our 5-minute quickstart guide</a></li><li><strong>Video walkthrough</strong>: <a href='{video_tutorial_url}'>Watch a 3-minute demo</a></li><li><strong>Live help</strong>: <a href='{book_call_url}'>Book a free 15-minute setup call</a></li></ul><p>Your {trial_plan} trial clock is ticking -- do not miss out on what {company_name} could achieve!</p>",
      ),

      // 7. Sales/success alert condition
      n("7", "condition", "Check if enterprise lead needs escalation", 1480, {
        conditionData: [
          { variable: "is_enterprise_lead", operator: "equals", value: "true" },
        ],
      }),

      // 8. Slack alert
      slackNode(
        "8",
        "Alert sales team of no-login lead (Slack)",
        1720,
        ":warning: *Trial No-Login Alert -- {urgency_level} Priority*\nContact: {user_first_name} {user_last_name} ({user_email})\nCompany: {company_name} | Title: {job_title}\nPlan: {trial_plan}\nSigned up: {days_since_signup} days ago ({hours_since_signup}h)\nEnterprise lead: {is_enterprise_lead}\nRescue approach: {rescue_approach}\n\nTwo rescue emails sent. Please reach out personally for a demo or onboarding call.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1221. TRIAL USER RETARGETING AUDIENCE (7 nodes)
  // ===================================================================

  "Trial User Retargeting Audience": {
    summary:
      "Runs daily to segment trial users from HubSpot, hashes user data for privacy compliance, syncs custom audiences to Meta Ads and Google Ads, selects engagement-based ad creatives in code, and logs campaign activation details to Sheets.",
    nodes: [
      trigger("Schedule: Daily trial user audience sync", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Fires daily at 6 AM to build retargeting audiences from trial users, sync hashed data to Meta and Google ad platforms, and activate engagement-appropriate ad creatives",
        setup:
          "Configure HubSpot API for trial user segmentation. Set up Meta Ads API and Google Ads API for audience syncing. Provide Google Sheets for campaign logging. Ensure GDPR-compliant data hashing.",
        inputFields: [],
      }),

      // 2. HubSpot trial user segment builder
      hubspotPostNode(
        "2",
        "Build trial user segment (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"subscriber"},{"propertyName":"hs_analytics_num_page_views","operator":"GTE","value":"1"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","hs_analytics_num_page_views","hs_analytics_last_visit_timestamp","trial_plan","hs_analytics_num_visits"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "results", customName: "trial_users_raw", type: "string" },
          { name: "total", customName: "trial_user_count", type: "number" },
          {
            name: "results[0].properties.email",
            customName: "sample_email",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "sample_page_views",
            type: "number",
          },
        ],
      ),

      // 3. User data hasher runCode
      n("3", "runCode", "Hash user data for privacy compliance", 520, {
        runCodeDetails: {
          code: `// Hash email addresses for ad platform audience matching (SHA-256)
const users = JSON.parse(variables.trial_users_raw || '[]');
const crypto = require('crypto');
const hashedEmails = [];
const engagementBuckets = { high: 0, medium: 0, low: 0 };
users.forEach(user => {
  const email = (user.properties?.email || '').toLowerCase().trim();
  if (email) {
    const hash = crypto.createHash('sha256').update(email).digest('hex');
    const pageViews = Number(user.properties?.hs_analytics_num_page_views) || 0;
    const visits = Number(user.properties?.hs_analytics_num_visits) || 0;
    const bucket = pageViews >= 10 ? 'high' : pageViews >= 3 ? 'medium' : 'low';
    engagementBuckets[bucket]++;
    hashedEmails.push({ hash: hash, engagement: bucket });
  }
});
const metaPayload = hashedEmails.map(h => h.hash);
const highEngagementHashes = hashedEmails.filter(h => h.engagement === 'high').map(h => h.hash);
return { hashed_emails_meta: JSON.stringify(metaPayload), hashed_emails_google: JSON.stringify(metaPayload), high_engagement_hashes: JSON.stringify(highEngagementHashes), total_hashed: hashedEmails.length, bucket_high: engagementBuckets.high, bucket_medium: engagementBuckets.medium, bucket_low: engagementBuckets.low };`,
          outputs: [
            { name: "hashed_emails_meta", type: "string" },
            { name: "hashed_emails_google", type: "string" },
            { name: "high_engagement_hashes", type: "string" },
            { name: "total_hashed", type: "number" },
            { name: "bucket_high", type: "number" },
            { name: "bucket_medium", type: "number" },
            { name: "bucket_low", type: "number" },
          ],
        },
      }),

      // 4. Meta custom audience sync API
      n("4", "hitApi", "Sync audience to Meta Ads", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_trial_audience_id}/users",
          headers: [
            { key: "Authorization", value: "Bearer {meta_ads_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "payload",
              value: '{"schema":"EMAIL_SHA256","data":{hashed_emails_meta}}',
            },
          ],
          variables: [
            {
              name: "audience_id",
              customName: "meta_audience_id",
              type: "string",
            },
            {
              name: "num_received",
              customName: "meta_records_synced",
              type: "number",
            },
          ],
        },
      }),

      // 5. Google Ads audience sync API
      n("5", "hitApi", "Sync audience to Google Ads", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/offlineUserDataJobs",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
          ],
          body: [
            { key: "type", value: "CUSTOMER_MATCH_USER_LIST" },
            {
              key: "customerMatchUserListMetadata",
              value:
                '{"userList":"customers/{google_ads_customer_id}/userLists/{trial_user_list_id}"}',
            },
            {
              key: "operations",
              value: '{"create":{"userIdentifiers":{hashed_emails_google}}}',
            },
          ],
          variables: [
            {
              name: "resourceName",
              customName: "google_job_resource",
              type: "string",
            },
            {
              name: "status",
              customName: "google_sync_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Engagement-based ad creative selector runCode
      n("6", "runCode", "Select ad creatives by engagement level", 1240, {
        runCodeDetails: {
          code: `// Map engagement buckets to appropriate ad creative strategies
const high = Number(variables.bucket_high) || 0;
const medium = Number(variables.bucket_medium) || 0;
const low = Number(variables.bucket_low) || 0;
const total = high + medium + low;
const creativeMatrix = {
  high_engagement: {
    count: high,
    message: 'Ready to convert -- show pricing, testimonials, and trial-to-paid CTAs',
    ad_type: 'conversion',
    budget_weight: 0.5,
  },
  medium_engagement: {
    count: medium,
    message: 'Nurture with feature highlights, case studies, and demo invites',
    ad_type: 'consideration',
    budget_weight: 0.3,
  },
  low_engagement: {
    count: low,
    message: 'Re-engage with value props, quick-start guides, and educational content',
    ad_type: 'awareness',
    budget_weight: 0.2,
  },
};
const recommended_daily_budget = total > 50 ? 100 : total > 20 ? 50 : 25;
return { creative_matrix: JSON.stringify(creativeMatrix), recommended_budget: recommended_daily_budget, audience_total: total, dominant_segment: high >= medium && high >= low ? 'high' : medium >= low ? 'medium' : 'low' };`,
          outputs: [
            { name: "creative_matrix", type: "string" },
            { name: "recommended_budget", type: "number" },
            { name: "audience_total", type: "number" },
            { name: "dominant_segment", type: "string" },
          ],
        },
      }),

      // 7. Campaign activation Sheets logger
      sheetsNode(
        "7",
        "Log campaign activation (Sheets)",
        1480,
        "Retargeting_Log",
        '[["{trial_user_count}","{total_hashed}","{bucket_high}","{bucket_medium}","{bucket_low}","{meta_records_synced}","{google_sync_status}","{dominant_segment}","${recommended_budget}/day","{creative_matrix}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1222. UGC CREATOR RECRUITMENT (8 nodes)
  // ===================================================================

  "UGC Creator Recruitment": {
    summary:
      "Scans HubSpot on schedule for highly engaged customers, scores their eligibility for UGC creation in code, generates personalized recruitment briefs with AI, sends recruitment emails, processes applications via condition, sends onboarding kits, and creates creator profiles in HubSpot CRM.",
    nodes: [
      trigger("Schedule: Identify potential UGC creators", "schedule", {
        schedule: "0 10 * * 2",
        howItStarts:
          "Fires every Tuesday at 10 AM to identify highly engaged customers who could be recruited as UGC creators, scoring their eligibility and initiating personalized outreach",
        setup:
          "Configure HubSpot API for customer engagement data. Set up OpenAI API for recruitment brief generation. Configure email sending domain. Define eligibility criteria thresholds.",
        inputFields: [],
      }),

      // 2. HubSpot engaged customer identifier
      hubspotPostNode(
        "2",
        "Find highly engaged customers (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_email_open","operator":"GTE","value":"10"},{"propertyName":"num_associated_deals","operator":"GTE","value":"2"},{"propertyName":"hs_analytics_num_page_views","operator":"GTE","value":"20"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","hs_email_open","num_associated_deals","hs_analytics_num_page_views","hs_social_twitter_clicks","total_revenue"]',
          },
          { key: "limit", value: "25" },
        ],
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "company",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_open",
            customName: "email_opens",
            type: "number",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "purchases",
            type: "number",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "total_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "page_views",
            type: "number",
          },
          { name: "total", customName: "candidates_found", type: "number" },
        ],
      ),

      // 3. Eligibility scorer runCode
      n("3", "runCode", "Score UGC creator eligibility", 520, {
        runCodeDetails: {
          code: `// Score customer eligibility for UGC creator program
const opens = Number(variables.email_opens) || 0;
const purchases = Number(variables.purchases) || 0;
const pageViews = Number(variables.page_views) || 0;
const revenue = parseFloat(variables.total_revenue) || 0;
let score = 0;
score += Math.min(opens / 5, 20);
score += Math.min(purchases * 10, 30);
score += Math.min(pageViews / 10, 20);
score += Math.min(revenue / 100, 30);
score = Math.round(score);
const tier = score >= 70 ? 'platinum' : score >= 50 ? 'gold' : score >= 30 ? 'silver' : 'bronze';
const eligible = score >= 30;
const compensation = tier === 'platinum' ? '$200/video + product' : tier === 'gold' ? '$100/video + product' : tier === 'silver' ? 'Product + exposure' : 'Product only';
const content_types = tier === 'platinum' || tier === 'gold' ? 'Unboxing, Review, Tutorial, Testimonial' : 'Review, Testimonial';
return { eligibility_score: score, creator_tier: tier, is_eligible: eligible, compensation_offer: compensation, suggested_content_types: content_types };`,
          outputs: [
            { name: "eligibility_score", type: "number" },
            { name: "creator_tier", type: "string" },
            { name: "is_eligible", type: "boolean" },
            { name: "compensation_offer", type: "string" },
            { name: "suggested_content_types", type: "string" },
          ],
        },
      }),

      // 4. AI recruitment brief generator
      aiNode(
        "4",
        "AI: Generate personalized recruitment brief",
        760,
        "You are a UGC program manager. Write a personalized recruitment email inviting this engaged customer to join your UGC creator program. Highlight their loyalty and engagement, explain the program benefits and compensation, describe what kind of content you are looking for, and make it feel exclusive. Respond JSON: {email_subject: string, email_body_html: string, program_pitch: string, next_steps: string}",
        "Customer: {first_name} {last_name} from {company}. Email opens: {email_opens}. Purchases: {purchases}. Revenue: {total_revenue}. Eligibility score: {eligibility_score}/100. Tier: {creator_tier}. Compensation: {compensation_offer}. Content types: {suggested_content_types}.",
        [
          { field: "email_subject", as: "recruitment_subject", type: "string" },
          { field: "email_body_html", as: "recruitment_body", type: "string" },
          { field: "program_pitch", as: "program_pitch", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
        ],
      ),

      // 5. Recruitment email sender
      emailNode(
        "5",
        "Send UGC recruitment email",
        1000,
        "{customer_email}",
        "{recruitment_subject}",
        "{recruitment_body}",
      ),

      // 6. Application processor condition
      n("6", "condition", "Check if creator applied", 1240, {
        conditionData: [
          {
            variable: "application_received",
            operator: "equals",
            value: "true",
          },
        ],
      }),

      // 7. Creator onboarding kit email
      emailNode(
        "7",
        "Send creator onboarding kit",
        1480,
        "{customer_email}",
        "Welcome to the Creator Program, {first_name}! Here is your onboarding kit",
        "<h2>You are officially a {creator_tier}-tier creator!</h2><p>Hi {first_name},</p><p>Welcome to our UGC Creator Program! We are thrilled to have you on board.</p><h3>Your Creator Kit:</h3><ul><li><strong>Compensation</strong>: {compensation_offer}</li><li><strong>Content Types</strong>: {suggested_content_types}</li><li><strong>Brand Guidelines</strong>: <a href='{brand_guidelines_url}'>Download here</a></li><li><strong>Content Brief Template</strong>: <a href='{brief_template_url}'>Access template</a></li><li><strong>Creator Portal</strong>: <a href='{creator_portal_url}'>Submit content here</a></li></ul><h3>Next Steps:</h3><p>{next_steps}</p><p>Questions? Reach out to your dedicated creator manager anytime.</p>",
      ),

      // 8. CRM creator profile HubSpot
      hubspotPostNode(
        "8",
        "Create creator profile in CRM (HubSpot)",
        1720,
        "/crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"ugc_creator_status":"active","ugc_creator_tier":"{creator_tier}","ugc_eligibility_score":"{eligibility_score}","ugc_compensation":"{compensation_offer}","ugc_content_types":"{suggested_content_types}"}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.ugc_creator_status",
            customName: "creator_status_confirmed",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1223. UGC RIGHTS REQUEST PIPELINE (8 nodes)
  // ===================================================================

  "UGC Rights Request Pipeline": {
    summary:
      "Takes a manual UGC discovery input, captures content metadata in code, looks up the creator profile in HubSpot, drafts a legal rights request with AI, sends for legal review via approval gate, sends the rights request email, processes the response via condition, and logs to a rights database in Sheets.",
    nodes: [
      trigger("Manual: UGC content discovered for rights request", "manual", {
        howItStarts:
          "Manually triggered when a team member discovers user-generated content that the brand wants to license or reuse, initiating the rights request pipeline with legal review and creator outreach",
        setup:
          "Configure HubSpot API for creator profile lookup. Set up OpenAI API for legal-tone drafting. Configure email sending. Set up Google Sheets for rights database. Define legal team approval contacts.",
        inputFields: [
          {
            name: "content_url",
            type: "string",
            example: "https://tiktok.com/@creator/video/1234567",
          },
          { name: "platform", type: "string", example: "TikTok" },
          {
            name: "creator_handle",
            type: "string",
            example: "@creator_handle",
          },
          {
            name: "content_description",
            type: "string",
            example: "Product unboxing and positive review, 45 seconds",
          },
          {
            name: "intended_use",
            type: "string",
            example: "Paid social ads and website testimonial",
          },
        ],
      }),

      // 2. Content metadata capturer runCode
      n("2", "runCode", "Capture and structure content metadata", 280, {
        runCodeDetails: {
          code: `// Structure UGC content metadata for rights request pipeline
const url = variables.content_url || '';
const platform = variables.platform || 'unknown';
const handle = variables.creator_handle || '';
const description = variables.content_description || '';
const use = variables.intended_use || '';
const contentId = 'UGC-' + Date.now().toString(36).toUpperCase();
const usageTypes = use.split(',').map(u => u.trim());
const needsExclusivity = usageTypes.some(u => u.toLowerCase().includes('paid') || u.toLowerCase().includes('ad'));
const licenseDuration = needsExclusivity ? '12 months exclusive' : '6 months non-exclusive';
const estimatedValue = needsExclusivity ? '$500-$1000' : '$100-$300';
const discoveryDate = new Date().toISOString().split('T')[0];
return { content_id: contentId, usage_types: usageTypes.join('; '), needs_exclusivity: needsExclusivity, license_duration: licenseDuration, estimated_compensation: estimatedValue, discovery_date: discoveryDate };`,
          outputs: [
            { name: "content_id", type: "string" },
            { name: "usage_types", type: "string" },
            { name: "needs_exclusivity", type: "boolean" },
            { name: "license_duration", type: "string" },
            { name: "estimated_compensation", type: "string" },
            { name: "discovery_date", type: "string" },
          ],
        },
      }),

      // 3. Creator profile lookup HubSpot
      hubspotPostNode(
        "3",
        "Look up creator profile (HubSpot)",
        520,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"social_handle","operator":"CONTAINS_TOKEN","value":"{creator_handle}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","social_handle","ugc_creator_status","ugc_creator_tier","company"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "creator_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "creator_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "creator_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "creator_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.ugc_creator_tier",
            customName: "creator_tier",
            type: "string",
          },
          { name: "total", customName: "creator_found", type: "number" },
        ],
      ),

      // 4. AI rights request drafter
      aiNode(
        "4",
        "AI: Draft rights request message",
        760,
        "You are a brand partnerships specialist with legal expertise. Draft a professional and friendly rights request email to a content creator asking permission to license their content. Include: compliment on their content, specific usage rights being requested, license duration, compensation offer, and clear next steps. The tone should be respectful and make the creator feel valued. Respond JSON: {email_subject: string, email_body_html: string, legal_summary: string, key_terms: string}",
        "Creator: {creator_first_name} {creator_last_name} ({creator_handle}). Platform: {platform}. Content: {content_description}. Content URL: {content_url}. Intended use: {usage_types}. License: {license_duration}. Exclusivity: {needs_exclusivity}. Compensation range: {estimated_compensation}. Creator tier: {creator_tier}.",
        [
          { field: "email_subject", as: "rights_subject", type: "string" },
          { field: "email_body_html", as: "rights_body", type: "string" },
          { field: "legal_summary", as: "legal_summary", type: "string" },
          { field: "key_terms", as: "key_terms", type: "string" },
        ],
      ),

      // 5. Legal review gate approval
      n("5", "approvalGate", "Legal team review of rights request", 1000, {
        approvalPrompt:
          "UGC Rights Request -- Legal Review Required\n\nContent ID: {content_id}\nCreator: {creator_first_name} {creator_last_name} ({creator_handle})\nPlatform: {platform}\nContent: {content_description}\nContent URL: {content_url}\n\nProposed Terms:\n- Usage: {usage_types}\n- License: {license_duration}\n- Exclusivity: {needs_exclusivity}\n- Compensation: {estimated_compensation}\n\nLegal Summary: {legal_summary}\nKey Terms: {key_terms}\n\nPlease approve to send the rights request to the creator.",
      }),

      // 6. Rights request sender email
      emailNode(
        "6",
        "Send rights request to creator",
        1240,
        "{creator_email}",
        "{rights_subject}",
        "{rights_body}",
      ),

      // 7. Response processor condition
      n("7", "condition", "Check creator response status", 1480, {
        conditionData: [
          { variable: "rights_granted", operator: "equals", value: "true" },
        ],
      }),

      // 8. Rights database logger Sheets
      sheetsNode(
        "8",
        "Log to rights database (Sheets)",
        1720,
        "UGC_Rights_Database",
        '[["{content_id}","{creator_first_name} {creator_last_name}","{creator_handle}","{creator_email}","{platform}","{content_url}","{content_description}","{usage_types}","{license_duration}","{needs_exclusivity}","{estimated_compensation}","{rights_granted}","{discovery_date}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1224. UTM LINK BUILDER AND TRACKER (8 nodes)
  // ===================================================================

  "UTM Link Builder and Tracker": {
    summary:
      "Takes a manual link request, validates UTM parameters in code, builds the full UTM URL in code, generates a short link via Bitly, registers the link in Sheets, sets up click monitoring logic, checks for performance alerts via condition, and reports weekly performance on Slack.",
    nodes: [
      trigger("Manual: Create tracked UTM link", "manual", {
        howItStarts:
          "Manually triggered when a team member needs a new tracked UTM link for a campaign, initiating parameter validation, link creation, shortening, and performance monitoring setup",
        setup:
          "Configure Bitly API token for link shortening. Set up Google Sheets for link registry. Provide Slack webhook for performance reporting. Define UTM parameter naming conventions.",
        inputFields: [
          {
            name: "destination_url",
            type: "string",
            example: "https://yoursite.com/landing-page",
          },
          {
            name: "campaign_name",
            type: "string",
            example: "q2_product_launch",
          },
          { name: "campaign_source", type: "string", example: "facebook" },
          { name: "campaign_medium", type: "string", example: "paid_social" },
          { name: "campaign_content", type: "string", example: "video_ad_v1" },
          {
            name: "campaign_term",
            type: "string",
            example: "project_management",
          },
        ],
      }),

      // 2. UTM parameter validator runCode
      n("2", "runCode", "Validate UTM parameters", 280, {
        runCodeDetails: {
          code: `// Validate and sanitize UTM parameters
const url = variables.destination_url || '';
const name = (variables.campaign_name || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_');
const source = (variables.campaign_source || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_');
const medium = (variables.campaign_medium || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_');
const content = (variables.campaign_content || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_');
const term = (variables.campaign_term || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_');
const errors = [];
if (!url.startsWith('http')) errors.push('Invalid destination URL');
if (!name) errors.push('Campaign name is required');
if (!source) errors.push('Campaign source is required');
if (!medium) errors.push('Campaign medium is required');
const isValid = errors.length === 0;
const validationMessage = isValid ? 'All parameters validated successfully' : 'Errors: ' + errors.join(', ');
return { clean_name: name, clean_source: source, clean_medium: medium, clean_content: content, clean_term: term, is_valid: isValid, validation_message: validationMessage };`,
          outputs: [
            { name: "clean_name", type: "string" },
            { name: "clean_source", type: "string" },
            { name: "clean_medium", type: "string" },
            { name: "clean_content", type: "string" },
            { name: "clean_term", type: "string" },
            { name: "is_valid", type: "boolean" },
            { name: "validation_message", type: "string" },
          ],
        },
      }),

      // 3. UTM link builder runCode
      n("3", "runCode", "Build full UTM URL", 520, {
        runCodeDetails: {
          code: `// Construct the full UTM-tagged URL
const base = variables.destination_url || '';
const separator = base.includes('?') ? '&' : '?';
const params = [];
if (variables.clean_name) params.push('utm_campaign=' + encodeURIComponent(variables.clean_name));
if (variables.clean_source) params.push('utm_source=' + encodeURIComponent(variables.clean_source));
if (variables.clean_medium) params.push('utm_medium=' + encodeURIComponent(variables.clean_medium));
if (variables.clean_content) params.push('utm_content=' + encodeURIComponent(variables.clean_content));
if (variables.clean_term) params.push('utm_term=' + encodeURIComponent(variables.clean_term));
const fullUrl = base + separator + params.join('&');
const linkId = 'UTM-' + Date.now().toString(36).toUpperCase();
return { full_utm_url: fullUrl, link_id: linkId, param_count: params.length };`,
          outputs: [
            { name: "full_utm_url", type: "string" },
            { name: "link_id", type: "string" },
            { name: "param_count", type: "number" },
          ],
        },
      }),

      // 4. Short link generator Bitly API
      n("4", "hitApi", "Generate short link (Bitly)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api-ssl.bitly.com/v4/shorten",
          headers: [
            { key: "Authorization", value: "Bearer {bitly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "long_url", value: "{full_utm_url}" },
            { key: "domain", value: "bit.ly" },
          ],
          variables: [
            { name: "id", customName: "bitly_id", type: "string" },
            { name: "link", customName: "short_url", type: "string" },
            {
              name: "long_url",
              customName: "confirmed_long_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. Link registry writer Sheets
      sheetsNode(
        "5",
        "Register link in tracking sheet (Sheets)",
        1000,
        "UTM_Link_Registry",
        '[["{link_id}","{short_url}","{full_utm_url}","{clean_name}","{clean_source}","{clean_medium}","{clean_content}","{clean_term}","{bitly_id}","active","0","=NOW()"]]',
      ),

      // 6. Click monitoring setup runCode
      n("6", "runCode", "Set up click monitoring thresholds", 1240, {
        runCodeDetails: {
          code: `// Define monitoring thresholds and alert rules
const source = variables.clean_source || '';
const medium = variables.clean_medium || '';
let dailyClickGoal = 50;
let alertThresholdLow = 10;
let alertThresholdHigh = 500;
if (medium.includes('paid')) {
  dailyClickGoal = 200;
  alertThresholdLow = 50;
  alertThresholdHigh = 2000;
} else if (medium.includes('email')) {
  dailyClickGoal = 100;
  alertThresholdLow = 20;
  alertThresholdHigh = 1000;
} else if (medium.includes('social') && !medium.includes('paid')) {
  dailyClickGoal = 30;
  alertThresholdLow = 5;
  alertThresholdHigh = 300;
}
const monitoringConfig = {
  daily_goal: dailyClickGoal,
  low_threshold: alertThresholdLow,
  high_threshold: alertThresholdHigh,
  check_frequency: 'daily',
};
return { daily_click_goal: dailyClickGoal, alert_low: alertThresholdLow, alert_high: alertThresholdHigh, monitoring_config: JSON.stringify(monitoringConfig) };`,
          outputs: [
            { name: "daily_click_goal", type: "number" },
            { name: "alert_low", type: "number" },
            { name: "alert_high", type: "number" },
            { name: "monitoring_config", type: "string" },
          ],
        },
      }),

      // 7. Performance alert condition
      n("7", "condition", "Check if performance needs attention", 1480, {
        conditionData: [
          {
            variable: "daily_click_goal",
            operator: "greater_than",
            value: "100",
          },
        ],
      }),

      // 8. Weekly performance reporter Slack
      slackNode(
        "8",
        "Report link performance (Slack)",
        1720,
        ":link: *UTM Link Created and Monitoring Active*\nLink ID: {link_id}\nShort URL: {short_url}\nCampaign: {clean_name}\nSource/Medium: {clean_source} / {clean_medium}\nContent: {clean_content}\n\nMonitoring Thresholds:\n- Daily goal: {daily_click_goal} clicks\n- Low alert: <{alert_low} clicks\n- High alert: >{alert_high} clicks\n\nFull URL: {full_utm_url}\nValidation: {validation_message}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1225. UNBOXING EXPERIENCE VIDEO (8 nodes)
  // ===================================================================

  "Unboxing Experience Video": {
    summary:
      "Captures product launch events via webhook, extracts product data with AI, generates an unboxing video script with AI, renders the video via Kling AI, checks render quality, sends for human approval, publishes to TikTok/Instagram, and logs performance tracking in Sheets.",
    nodes: [
      trigger("Webhook: New product launch event", "webhook_app", {
        source: "product_catalog",
        event: "product.launched",
        howItStarts:
          "Fires when a new product is launched in the catalog, automatically generating an unboxing experience video script, rendering it, and publishing across social platforms",
        setup:
          "Configure product catalog webhook for launch events. Set up OpenAI API for script generation. Provide Kling AI API for video rendering. Configure TikTok Business API. Set up Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. AI extract product data
      aiNode(
        "2",
        "AI: Extract product launch details",
        280,
        "You are a data extraction specialist for product catalogs. Extract product launch details from this webhook regardless of format. Find the product name, category, price, key features (list), hero image URL, product description, target audience, and launch date. Respond JSON: {product_name: string, category: string, price: string, key_features: string, hero_image_url: string, description: string, target_audience: string, launch_date: string, product_id: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "category", as: "product_category", type: "string" },
          { field: "price", as: "product_price", type: "string" },
          { field: "key_features", as: "key_features", type: "string" },
          { field: "hero_image_url", as: "hero_image_url", type: "string" },
          { field: "description", as: "product_description", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "launch_date", as: "launch_date", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
        ],
      ),

      // 3. AI unboxing script generator
      aiNode(
        "3",
        "AI: Generate unboxing video script",
        520,
        "You are a product video producer specializing in unboxing content. Create a compelling unboxing video script that builds anticipation, showcases the product packaging and presentation, highlights key features during the reveal, and drives purchase intent. Include: opening hook, packaging reveal moment, feature callouts with visual directions, first impression reaction, and closing CTA. Respond JSON: {full_script: string, hook_line: string, reveal_moment: string, feature_callouts: string, closing_cta: string, visual_directions: string, music_mood: string, estimated_duration: number}",
        "Product: {product_name}. Category: {product_category}. Price: {product_price}. Features: {key_features}. Description: {product_description}. Audience: {target_audience}.",
        [
          { field: "full_script", as: "unboxing_script", type: "string" },
          { field: "hook_line", as: "hook_line", type: "string" },
          { field: "reveal_moment", as: "reveal_moment", type: "string" },
          { field: "feature_callouts", as: "feature_callouts", type: "string" },
          { field: "closing_cta", as: "closing_cta", type: "string" },
          {
            field: "visual_directions",
            as: "visual_directions",
            type: "string",
          },
          { field: "music_mood", as: "music_mood", type: "string" },
          { field: "estimated_duration", as: "video_duration", type: "number" },
        ],
      ),

      // 4. Kling video renderer API
      n("4", "hitApi", "Render unboxing video (Kling AI)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "prompt",
              value:
                "{visual_directions}. Product: {product_name}. Unboxing reveal: {reveal_moment}. Style: cinematic product unboxing with clean aesthetics",
            },
            { key: "duration", value: "10" },
            { key: "aspect_ratio", value: "9:16" },
            { key: "mode", value: "high_quality" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "render_task_id",
              type: "string",
            },
            {
              name: "data.task_status",
              customName: "render_status",
              type: "string",
            },
            {
              name: "data.task_result.videos[0].url",
              customName: "video_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. Render quality check condition
      n("5", "condition", "Verify video render completed", 1000, {
        conditionData: [
          { variable: "render_status", operator: "equals", value: "completed" },
        ],
      }),

      // 6. Human approval gate
      n("6", "approvalGate", "Review unboxing video", 1240, {
        approvalPrompt:
          "Unboxing video ready for review:\n\nProduct: {product_name} ({product_category})\nPrice: {product_price}\n\nScript: {unboxing_script}\nHook: {hook_line}\nReveal: {reveal_moment}\nCTA: {closing_cta}\n\nVideo URL: {video_url}\nEstimated duration: {video_duration}s\n\nApprove to publish across TikTok and Instagram.",
      }),

      // 7. TikTok/Instagram publisher API
      n("7", "hitApi", "Publish to TikTok and Instagram", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://open.tiktokapis.com/v2/post/publish/video/init/",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "post_info",
              value:
                '{"title":"{hook_line} | {product_name} Unboxing #{product_category} #unboxing #newproduct #productlaunch","privacy_level":"PUBLIC_TO_EVERYONE","disable_duet":false,"disable_comment":false}',
            },
            {
              key: "source_info",
              value: '{"source":"PULL_FROM_URL","video_url":"{video_url}"}',
            },
          ],
          variables: [
            {
              name: "data.publish_id",
              customName: "tiktok_publish_id",
              type: "string",
            },
            {
              name: "data.status",
              customName: "publish_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Performance tracker Sheets
      sheetsNode(
        "8",
        "Log video performance tracking (Sheets)",
        1720,
        "Unboxing_Videos",
        '[["{product_id}","{product_name}","{product_category}","{product_price}","{hook_line}","{tiktok_publish_id}","{publish_status}","{video_duration}","{launch_date}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1226. UNDERPERFORMER PAUSE QUEUE (8 nodes)
  // ===================================================================

  "Underperformer Pause Queue": {
    summary:
      "Scans Google Ads daily for underperforming campaigns, detects underperformance in code, enriches with historical context, generates AI analysis with recommendations, classifies by priority, pauses critical underperformers via Google Ads API, and notifies the team on Slack.",
    nodes: [
      trigger("Schedule: Daily underperformer scan", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Fires daily at 7 AM to scan Google Ads campaigns for underperformers, analyze their decline patterns, auto-pause critical waste, and alert the team with AI-powered recommendations",
        setup:
          "Configure Google Ads API with campaign read and update permissions. Set up OpenAI API for analysis. Provide Slack webhook for team notifications. Define performance thresholds (CPA, ROAS, CTR).",
        inputFields: [],
      }),

      // 2. Google Ads API performance scanner
      n("2", "hitApi", "Scan campaign performance (Google Ads)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.id, campaign.name, campaign.status, metrics.cost_micros, metrics.conversions, metrics.clicks, metrics.impressions, metrics.ctr, metrics.average_cpc FROM campaign WHERE campaign.status = 'ENABLED' AND segments.date DURING LAST_7_DAYS",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "campaign_data_raw",
              type: "string",
            },
            {
              name: "results[0].campaign.id",
              customName: "sample_campaign_id",
              type: "string",
            },
            {
              name: "results[0].campaign.name",
              customName: "sample_campaign_name",
              type: "string",
            },
            {
              name: "results[0].metrics.cost_micros",
              customName: "sample_cost_micros",
              type: "number",
            },
            {
              name: "results[0].metrics.conversions",
              customName: "sample_conversions",
              type: "number",
            },
            {
              name: "results[0].metrics.clicks",
              customName: "sample_clicks",
              type: "number",
            },
            {
              name: "results[0].metrics.ctr",
              customName: "sample_ctr",
              type: "number",
            },
          ],
        },
      }),

      // 3. Underperformance detector runCode
      n("3", "runCode", "Detect underperforming campaigns", 520, {
        runCodeDetails: {
          code: `// Identify underperforming campaigns based on ROAS, CPA, and CTR thresholds
const campaigns = JSON.parse(variables.campaign_data_raw || '[]');
const underperformers = [];
const maxCpa = 50;
const minCtr = 0.005;
const minRoas = 2.0;
campaigns.forEach(c => {
  const cost = (c.metrics?.cost_micros || 0) / 1000000;
  const conversions = c.metrics?.conversions || 0;
  const clicks = c.metrics?.clicks || 0;
  const impressions = c.metrics?.impressions || 0;
  const ctr = impressions > 0 ? clicks / impressions : 0;
  const cpa = conversions > 0 ? cost / conversions : cost > 0 ? 9999 : 0;
  const revenue = conversions * 75;
  const roas = cost > 0 ? revenue / cost : 0;
  if (cost > 10 && (cpa > maxCpa || ctr < minCtr || roas < minRoas)) {
    underperformers.push({
      id: c.campaign?.id,
      name: c.campaign?.name,
      cost: cost.toFixed(2),
      conversions: conversions,
      cpa: cpa.toFixed(2),
      ctr: (ctr * 100).toFixed(2),
      roas: roas.toFixed(2),
      issue: cpa > maxCpa ? 'high_cpa' : ctr < minCtr ? 'low_ctr' : 'low_roas',
    });
  }
});
underperformers.sort((a, b) => parseFloat(b.cost) - parseFloat(a.cost));
const topUnderperformer = underperformers[0] || {};
return { underperformers_json: JSON.stringify(underperformers), underperformer_count: underperformers.length, top_underperformer_id: topUnderperformer.id || '', top_underperformer_name: topUnderperformer.name || '', top_issue: topUnderperformer.issue || 'none', total_wasted_spend: underperformers.reduce((s, u) => s + parseFloat(u.cost), 0).toFixed(2) };`,
          outputs: [
            { name: "underperformers_json", type: "string" },
            { name: "underperformer_count", type: "number" },
            { name: "top_underperformer_id", type: "string" },
            { name: "top_underperformer_name", type: "string" },
            { name: "top_issue", type: "string" },
            { name: "total_wasted_spend", type: "string" },
          ],
        },
      }),

      // 4. Historical context enricher runCode
      n("4", "runCode", "Enrich with historical performance context", 760, {
        runCodeDetails: {
          code: `// Determine if underperformance is a trend or anomaly
const count = Number(variables.underperformer_count) || 0;
const wastedSpend = parseFloat(variables.total_wasted_spend) || 0;
const topIssue = variables.top_issue || 'none';
const severityScore = Math.min(100, Math.round((count * 10) + (wastedSpend / 10)));
const trendAnalysis = count > 5 ? 'Systemic issue -- multiple campaigns underperforming simultaneously' : count > 2 ? 'Moderate concern -- several campaigns need attention' : count > 0 ? 'Isolated issue -- specific campaign optimization needed' : 'All campaigns healthy';
const recommendedAction = severityScore > 70 ? 'immediate_pause' : severityScore > 40 ? 'review_and_optimize' : 'monitor';
const budgetImpact = wastedSpend > 500 ? 'critical' : wastedSpend > 100 ? 'significant' : 'minor';
return { severity_score: severityScore, trend_analysis: trendAnalysis, recommended_action: recommendedAction, budget_impact: budgetImpact };`,
          outputs: [
            { name: "severity_score", type: "number" },
            { name: "trend_analysis", type: "string" },
            { name: "recommended_action", type: "string" },
            { name: "budget_impact", type: "string" },
          ],
        },
      }),

      // 5. AI analysis generator
      aiNode(
        "5",
        "AI: Analyze underperformers and recommend fixes",
        1000,
        "You are a Google Ads optimization expert. Analyze these underperforming campaigns and provide specific, actionable recommendations for each. Consider the issue type (high CPA, low CTR, low ROAS), severity, and budget impact. For each campaign, suggest: root cause hypothesis, immediate fix, long-term optimization, and estimated impact of the fix. Respond JSON: {analysis_summary: string, top_recommendations: string, estimated_savings: string, pause_candidates: string, optimize_candidates: string}",
        "Underperformers ({underperformer_count}): {underperformers_json}. Severity: {severity_score}/100. Trend: {trend_analysis}. Budget impact: {budget_impact}. Total wasted: ${total_wasted_spend}. Recommended action: {recommended_action}.",
        [
          { field: "analysis_summary", as: "analysis_summary", type: "string" },
          {
            field: "top_recommendations",
            as: "top_recommendations",
            type: "string",
          },
          {
            field: "estimated_savings",
            as: "estimated_savings",
            type: "string",
          },
          { field: "pause_candidates", as: "pause_candidates", type: "string" },
          {
            field: "optimize_candidates",
            as: "optimize_candidates",
            type: "string",
          },
        ],
      ),

      // 6. Priority classifier condition
      n("6", "condition", "Route by severity: pause or alert only", 1240, {
        conditionData: [
          {
            variable: "recommended_action",
            operator: "equals",
            value: "immediate_pause",
          },
        ],
      }),

      // 7. Google Ads pause executor API
      n(
        "7",
        "hitApi",
        "Pause critical underperformers (Google Ads)",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/campaigns/{top_underperformer_id}:mutate",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "developer-token", value: "{google_ads_dev_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "operation",
                value:
                  '{"update":{"resourceName":"customers/{google_ads_customer_id}/campaigns/{top_underperformer_id}","status":"PAUSED"},"updateMask":"status"}',
              },
            ],
            variables: [
              {
                name: "results[0].resourceName",
                customName: "paused_campaign_resource",
                type: "string",
              },
            ],
          },
        },
        380,
      ),

      // 8. Team notifier Slack
      slackNode(
        "8",
        "Notify team of underperformer actions (Slack)",
        1720,
        ":chart_with_downwards_trend: *Daily Underperformer Report*\n\nSeverity: {severity_score}/100 | Impact: {budget_impact}\nUnderperformers found: {underperformer_count}\nTotal wasted spend: ${total_wasted_spend}\n\nTop Issue: {top_underperformer_name} -- {top_issue}\nAction taken: {recommended_action}\n\n*AI Analysis:*\n{analysis_summary}\n\n*Top Recommendations:*\n{top_recommendations}\n\nEstimated savings from fixes: {estimated_savings}\nPause candidates: {pause_candidates}\nOptimize candidates: {optimize_candidates}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1227. UNENGAGED EMAIL SUNSET (8 nodes)
  // ===================================================================

  "Unengaged Email Sunset": {
    summary:
      "Scans HubSpot on schedule for long-term unengaged subscribers, checks deliverability status, sends a first re-engagement attempt email, waits 7 days, evaluates the second attempt decision via condition, executes the sunset suppression in HubSpot, and reports list health to Sheets.",
    nodes: [
      trigger("Schedule: Weekly unengaged subscriber sunset", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Fires every Monday at 8 AM to identify subscribers who have been unengaged for 90+ days, attempt re-engagement, and systematically sunset those who remain unresponsive to protect sender reputation",
        setup:
          "Configure HubSpot API with contact read and update permissions. Set up email sending domain. Configure Google Sheets for list health reporting. Define sunset criteria (default: 90 days no engagement).",
        inputFields: [],
      }),

      // 2. HubSpot unengaged subscriber identifier
      hubspotPostNode(
        "2",
        "Find unengaged subscribers (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_email_last_open_date","operator":"LT","value":"{ninety_days_ago_epoch}"},{"propertyName":"hs_email_optout","operator":"EQ","value":"false"},{"propertyName":"hs_email_sends_since_last_engagement","operator":"GTE","value":"5"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","hs_email_last_open_date","hs_email_sends_since_last_engagement","hs_email_last_click_date","lifecyclestage","hs_email_bounce"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "results[0].id", customName: "contact_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "subscriber_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "subscriber_name",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "last_open_date",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_sends_since_last_engagement",
            customName: "sends_since_engagement",
            type: "number",
          },
          {
            name: "results[0].properties.hs_email_last_click_date",
            customName: "last_click_date",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_bounce",
            customName: "bounce_status",
            type: "string",
          },
          { name: "total", customName: "unengaged_total", type: "number" },
        ],
      ),

      // 3. Deliverability verification condition
      n("3", "condition", "Verify email is deliverable", 520, {
        conditionData: [
          { variable: "bounce_status", operator: "not_equals", value: "true" },
        ],
      }),

      // 4. Re-engagement attempt 1 email
      emailNode(
        "4",
        "Send re-engagement attempt 1",
        760,
        "{subscriber_email}",
        "We miss you, {subscriber_name} -- should we keep in touch?",
        "<h2>It has been a while!</h2><p>Hi {subscriber_name},</p><p>We noticed it has been some time since you last engaged with our emails. We want to make sure we are sending you content that matters.</p><h3>Here is what you have been missing:</h3><ul><li>New features and product updates</li><li>Exclusive tips and insights</li><li>Special offers just for subscribers</li></ul><p><strong>Want to stay on the list?</strong> Simply click the button below to confirm you still want to hear from us:</p><p><a href='{reengagement_confirm_url}?contact={contact_id}' style='display:inline-block;background:#10B981;color:white;padding:12px 24px;border-radius:6px;text-decoration:none;font-weight:bold'>Yes, keep me subscribed!</a></p><p>If we do not hear from you in the next 7 days, we will remove you from our list to keep your inbox clean. No hard feelings!</p>",
      ),

      // 5. Delay 7 days
      n("5", "delayAction", "Wait 7 days for response", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 6. Re-engagement attempt 2 condition
      n("6", "condition", "Check if subscriber re-engaged", 1240, {
        conditionData: [
          {
            variable: "reengagement_confirmed",
            operator: "equals",
            value: "true",
          },
        ],
      }),

      // 7. Sunset executor HubSpot
      hubspotPostNode(
        "7",
        "Execute sunset suppression (HubSpot)",
        1480,
        "/crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_email_optout":"true","sunset_reason":"90_day_no_engagement","lifecyclestage":"other"}',
          },
        ],
        [
          { name: "id", customName: "sunset_contact_id", type: "string" },
          {
            name: "properties.hs_email_optout",
            customName: "confirmed_optout",
            type: "string",
          },
        ],
      ),

      // 8. List health reporter Sheets
      sheetsNode(
        "8",
        "Report list health metrics (Sheets)",
        1720,
        "Email_List_Health",
        '[["{unengaged_total}","{subscriber_email}","{last_open_date}","{sends_since_engagement}","{last_click_date}","{bounce_status}","{confirmed_optout}","sunset","90_day_no_engagement","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },
};
